import type {
  PrintCardAabb,
  PrintCardCompiledSizeContent,
  PrintCardDesignDocument,
  PrintCardDesignPage,
  PrintCardElement,
  PrintCardElementPosition,
  PrintCardGeometryBox,
  PrintCardGuideGeometry,
  PrintCardGuidePage,
  PrintCardOrientation,
  PrintCardSideDesign,
  PrintCardSizeLayout,
  PrintCardSizeSideLayout,
  PrintCardFontWeight,
  PrintCardTextElement,
  PrintCardTextFit,
  PrintCardTextSquashPart,
  PrintCardTextSquashStyle,
  PrintCardTextVariable,
  SmartGuideAxis,
  SmartGuideLine,
  SmartGuidesView,
  SmartGuideSpacing,
  SnapMoveInput,
  SnapMoveResult,
  SnapResizeInput,
  SnapResizeResult,
} from "./print-cards.interface";
import {
  PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT,
  PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_COMPANY_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_NAME_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_TAGLINE_PT,
  PRINT_CARD_DEFAULT_FONT_SIZE_TITLE_PT,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_COMPANY,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_NAME,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_TAGLINE,
  PRINT_CARD_DEFAULT_FONT_WEIGHT_TITLE,
  PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
  PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
  PRINT_CARD_DEFAULT_NFC_SIZE_CM,
  PRINT_CARD_DEFAULT_QR_SIZE_CM,
  PRINT_CARD_DEFAULT_SQUASH_SEPARATOR,
  PRINT_CARD_DEFAULT_TAGLINE_TEXT,
  PRINT_CARD_TEXT_HEIGHT_ADDRESS_CM,
  PRINT_CARD_TEXT_HEIGHT_BODY_CM,
  PRINT_CARD_TEXT_HEIGHT_CONTACT_CM,
  PRINT_CARD_TEXT_HEIGHT_NAME_CM,
  PRINT_CARD_TEXT_HEIGHT_SPLIT_TITLE_CM,
  PRINT_CARD_TEXT_HEIGHT_STACK_NAME_CM,
  PRINT_CARD_TEXT_HEIGHT_STACK_TITLE_CM,
  PRINT_CARD_TEXT_HEIGHT_TAGLINE_CM,
  PRINT_CARD_TEXT_HEIGHT_TITLE_CM,
} from "./print-cards.interface";

export const PRINT_CARD_MIN_SIZE_CM = 0;

const PRINT_CARD_BACK_LOGO_MAX_WIDTH_CM = 2.4;
const PRINT_CARD_BACK_LOGO_MAX_HEIGHT_CM = 1.2;
const LAYOUT_NAME_LINE_CM = PRINT_CARD_TEXT_HEIGHT_NAME_CM;
const LAYOUT_TITLE_LINE_CM = PRINT_CARD_TEXT_HEIGHT_TITLE_CM;
const LAYOUT_STACK_NAME_LINE_CM = PRINT_CARD_TEXT_HEIGHT_STACK_NAME_CM;
const LAYOUT_STACK_TITLE_LINE_CM = PRINT_CARD_TEXT_HEIGHT_STACK_TITLE_CM;
const LAYOUT_SPLIT_TITLE_LINE_CM = PRINT_CARD_TEXT_HEIGHT_SPLIT_TITLE_CM;
const LAYOUT_STANDARD_TITLE_GAP_CM = 0.05;
const LAYOUT_STANDARD_CONTACT_GAP_CM = 0.48;
const LAYOUT_STACK_TITLE_GAP_CM = 0.04;
const LAYOUT_ADDRESS_STACK_GAP_CM = 0.08;
const LAYOUT_ADDRESS_ROW_CM = PRINT_CARD_TEXT_HEIGHT_ADDRESS_CM;
const LAYOUT_TAGLINE_MAX_H_CM = PRINT_CARD_TEXT_HEIGHT_TAGLINE_CM;
const LAYOUT_CONTACT_LINE_CM = PRINT_CARD_TEXT_HEIGHT_CONTACT_CM;

export function printCardDefaultTextHeightCm(variable?: string): number {
  if (variable === "userName") {
    return PRINT_CARD_TEXT_HEIGHT_NAME_CM;
  }
  if (variable === "jobTitle") {
    return PRINT_CARD_TEXT_HEIGHT_TITLE_CM;
  }
  if (variable === "tagline") {
    return PRINT_CARD_TEXT_HEIGHT_TAGLINE_CM;
  }
  if (variable === "companyName" || variable === "companyAddress") {
    return PRINT_CARD_TEXT_HEIGHT_ADDRESS_CM;
  }
  if (variable === "phone" || variable === "email" || variable === "squash") {
    return PRINT_CARD_TEXT_HEIGHT_CONTACT_CM;
  }
  return PRINT_CARD_TEXT_HEIGHT_BODY_CM;
}

export function printCardElementDefaultName(
  el: Pick<PrintCardElement, "type"> & {
    variable?: string;
    squashParts?: PrintCardTextSquashPart[];
  },
): string {
  if (el.type === "text") {
    if (el.variable === "squash" && el.squashParts?.length) {
      return el.squashParts.map((part) => part.variable).join("+");
    }
    return el.variable?.trim() || "variable";
  }
  return el.type;
}

export const PRINT_CARD_PALETTE_BUILTIN_TEXT_VARIABLES = new Set<string>([
  "userName",
  "jobTitle",
  "phone",
  "email",
  "companyName",
  "companyAddress",
]);

export const PRINT_CARD_VARIABLE_PLACEHOLDER_KEY: Record<string, string> = {
  phone: "formattedPhoneNumber",
  email: "userEmail",
};

export function isPrintCardPaletteBuiltinTextVariable(
  variable: string | undefined | null,
): boolean {
  const key = variable?.trim() || "";
  return key.length > 0 && PRINT_CARD_PALETTE_BUILTIN_TEXT_VARIABLES.has(key);
}

function isPrintCardVariableKey(value: string): boolean {
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
}

function compilePrintCardVariableInner(
  variable: string | undefined,
  text?: string,
): string {
  const key = variable?.trim() || "";
  if (
    !key ||
    key === "tagline" ||
    key === "custom" ||
    key === "squash" ||
    !isPrintCardVariableKey(key)
  ) {
    const escaped = escapeDesignHtml(
      (text ?? "").replace(/\r\n/g, "\n").trim(),
    );
    const lines = escaped.split("\n");
    if (lines.length <= 1) {
      return escaped;
    }
    return lines
      .map(
        (line) =>
          `<span class="pc-text-line">${line.length > 0 ? line : "&nbsp;"}</span>`,
      )
      .join("");
  }
  const placeholder = PRINT_CARD_VARIABLE_PLACEHOLDER_KEY[key] ?? key;
  return `\${${placeholder}}`;
}

export function printCardTextElementSquashParts(
  el: PrintCardTextElement,
): PrintCardTextSquashPart[] | null {
  if (
    el.variable === "squash" &&
    el.squashParts &&
    el.squashParts.length >= 2
  ) {
    return el.squashParts;
  }
  return null;
}

export function printCardTextElementToSquashParts(
  el: PrintCardTextElement,
): PrintCardTextSquashPart[] {
  if (
    el.variable === "squash" &&
    el.squashParts &&
    el.squashParts.length >= 2
  ) {
    return el.squashParts.map((part) => ({ ...part }));
  }
  const style = {
    ...(typeof el.fontSizePt === "number" && el.fontSizePt > 0
      ? { fontSizePt: el.fontSizePt }
      : {}),
    ...(typeof el.fontWeight === "number" ? { fontWeight: el.fontWeight } : {}),
    ...(el.italic ? { italic: true as const } : {}),
    ...(el.uppercase ? { uppercase: true as const } : {}),
    ...(el.color?.trim() ? { color: el.color.trim() } : {}),
    ...(typeof el.lineHeight === "number" &&
    el.lineHeight >= 0 &&
    el.lineHeight !== 1
      ? { lineHeight: el.lineHeight }
      : {}),
  };
  const variable = el.variable?.trim() || "custom";
  if (
    variable === "custom" ||
    variable === "tagline" ||
    variable === "squash"
  ) {
    return [
      {
        variable: variable === "squash" ? "custom" : variable,
        text: el.text,
        ...style,
      },
    ];
  }
  if (isPrintCardVariableKey(variable)) {
    return [{ variable, ...style }];
  }
  return [{ variable: "custom", text: el.text, ...style }];
}

function parsePrintCardSquashStyleFromSpanAttrs(
  attrs: string,
): PrintCardTextSquashStyle {
  const styleAttr = attrs.match(/\bstyle\s*=\s*["']([^"']*)["']/i)?.[1] ?? "";
  const ptAttr = attrs.match(/\bdata-pc-pt\s*=\s*["']([^"']*)["']/i)?.[1];
  const ptFromAttr = ptAttr ? Number.parseFloat(ptAttr) : NaN;
  const ptFromStyle = Number.parseFloat(
    styleAttr.match(/font-size\s*:\s*([\d.]+)\s*pt/i)?.[1] ?? "",
  );
  const fontSizePt =
    Number.isFinite(ptFromAttr) && ptFromAttr > 0
      ? ptFromAttr
      : Number.isFinite(ptFromStyle) && ptFromStyle > 0
        ? ptFromStyle
        : undefined;
  const fontWeightRaw = Number.parseInt(
    styleAttr.match(/font-weight\s*:\s*(\d+)/i)?.[1] ?? "",
    10,
  );
  const fontWeight: PrintCardFontWeight | undefined =
    fontWeightRaw === 400 ||
    fontWeightRaw === 500 ||
    fontWeightRaw === 600 ||
    fontWeightRaw === 700
      ? fontWeightRaw
      : undefined;
  const color = styleAttr.match(/color\s*:\s*([^;]+)/i)?.[1]?.trim();
  const lineHeightMatch = styleAttr.match(/line-height\s*:\s*([\d.]+)\s*(%)?/i);
  const lineHeightParsed = lineHeightMatch
    ? Number.parseFloat(lineHeightMatch[1]) /
      (lineHeightMatch[2] === "%" ? 100 : 1)
    : NaN;
  const lineHeight =
    Number.isFinite(lineHeightParsed) &&
    lineHeightParsed >= 0 &&
    lineHeightParsed !== 1
      ? Math.round(lineHeightParsed * 100) / 100
      : undefined;
  return {
    ...(fontSizePt != null ? { fontSizePt } : {}),
    ...(fontWeight != null ? { fontWeight } : {}),
    ...(/font-style\s*:\s*italic/i.test(styleAttr)
      ? { italic: true as const }
      : {}),
    ...(/text-transform\s*:\s*uppercase/i.test(styleAttr)
      ? { uppercase: true as const }
      : {}),
    ...(color ? { color } : {}),
    ...(lineHeight != null ? { lineHeight } : {}),
  };
}

function compilePrintCardSquashInner(
  parts: PrintCardTextSquashPart[],
  separator: string,
  baseFontSizePt: number,
  separatorStyle?: PrintCardTextSquashStyle,
): string {
  const sepHtml = escapeDesignHtml(separator);
  const chunks: string[] = [];
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index];
    const partPt =
      typeof part.fontSizePt === "number" && part.fontSizePt > 0
        ? part.fontSizePt
        : baseFontSizePt;
    const partDecls: string[] = [
      `font-size: ${Math.round((partPt / baseFontSizePt) * 1000) / 1000}em`,
    ];
    if (typeof part.fontWeight === "number") {
      partDecls.push(`font-weight: ${part.fontWeight}`);
    }
    if (part.italic) {
      partDecls.push("font-style: italic");
    }
    if (part.uppercase) {
      partDecls.push("text-transform: uppercase");
    }
    if (part.color?.trim()) {
      partDecls.push(`color: ${part.color.trim()}`);
    }
    if (
      typeof part.lineHeight === "number" &&
      part.lineHeight >= 0 &&
      part.lineHeight !== 1
    ) {
      partDecls.push(`line-height: ${part.lineHeight}`);
    }
    chunks.push(
      `<span class="pc-squash-part" data-pc-part="${escapeDesignHtml(part.variable)}" data-pc-pt="${partPt}" style="${escapeDesignHtml(partDecls.join(";"))}">${compilePrintCardVariableInner(part.variable, part.text)}</span>`,
    );
    if (index < parts.length - 1) {
      const sepPt =
        typeof separatorStyle?.fontSizePt === "number" &&
        separatorStyle.fontSizePt > 0
          ? separatorStyle.fontSizePt
          : baseFontSizePt;
      const sepDecls: string[] = [
        `font-size: ${Math.round((sepPt / baseFontSizePt) * 1000) / 1000}em`,
      ];
      if (typeof separatorStyle?.fontWeight === "number") {
        sepDecls.push(`font-weight: ${separatorStyle.fontWeight}`);
      }
      if (separatorStyle?.italic) {
        sepDecls.push("font-style: italic");
      }
      if (separatorStyle?.uppercase) {
        sepDecls.push("text-transform: uppercase");
      }
      if (separatorStyle?.color?.trim()) {
        sepDecls.push(`color: ${separatorStyle.color.trim()}`);
      }
      if (
        typeof separatorStyle?.lineHeight === "number" &&
        separatorStyle.lineHeight >= 0 &&
        separatorStyle.lineHeight !== 1
      ) {
        sepDecls.push(`line-height: ${separatorStyle.lineHeight}`);
      }
      chunks.push(
        `<span class="pc-squash-sep" data-pc-pt="${sepPt}" style="${escapeDesignHtml(sepDecls.join(";"))}">${sepHtml}</span>`,
      );
    }
  }
  return chunks.join("");
}

function parsePrintCardSquashPartsFromAttrs(
  attrs: string,
  inner: string,
): {
  parts: PrintCardTextSquashPart[];
  separator: string;
  separatorStyle?: PrintCardTextSquashStyle;
} | null {
  const sepMatch = attrs.match(/\bdata-pc-squash-sep\s*=\s*["']([^"']*)["']/i);
  const separator =
    sepMatch != null
      ? (sepMatch[1] ?? "")
          .replace(/&quot;/g, '"')
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&amp;/g, "&")
      : PRINT_CARD_DEFAULT_SQUASH_SEPARATOR;
  const sepSpanMatch =
    /<span\b([^>]*\bclass=["'][^"']*\bpc-squash-sep\b[^"']*["'][^>]*)>/i.exec(
      inner,
    );
  const separatorStyle = sepSpanMatch
    ? parsePrintCardSquashStyleFromSpanAttrs(sepSpanMatch[1] ?? "")
    : undefined;
  const hasSeparatorStyle =
    separatorStyle != null && Object.keys(separatorStyle).length > 0;
  const partsAttr = attrs.match(
    /\bdata-pc-squash-parts\s*=\s*["']([^"']*)["']/i,
  )?.[1];
  const parts: PrintCardTextSquashPart[] = [];
  if (partsAttr) {
    for (const token of partsAttr.split(",")) {
      const variable = token.trim();
      if (
        variable !== "custom" &&
        variable !== "tagline" &&
        !isPrintCardVariableKey(variable)
      ) {
        continue;
      }
      const partRe = new RegExp(
        `<span\\b([^>]*\\bdata-pc-part\\s*=\\s*["']${variable}["'][^>]*)>([\\s\\S]*?)<\\/span>`,
        "i",
      );
      const partMatch = partRe.exec(inner);
      const partAttrs = partMatch?.[1] ?? "";
      const style = parsePrintCardSquashStyleFromSpanAttrs(partAttrs);
      const part: PrintCardTextSquashPart = {
        variable,
        ...style,
      };
      if (variable === "custom" || variable === "tagline") {
        const raw = (partMatch?.[2] ?? "")
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<[^>]+>/g, "");
        part.text = raw
          .replace(/&nbsp;/g, " ")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, "&");
      }
      parts.push(part);
    }
  }
  if (parts.length < 2) {
    return null;
  }
  return {
    parts,
    separator,
    ...(hasSeparatorStyle ? { separatorStyle } : {}),
  };
}

export function nextUniquePrintCardElementName(
  preferred: string,
  existingNames: Iterable<string>,
): string {
  const trimmed = preferred.trim();
  const numbered = /^(.*)\s+(\d+)$/.exec(trimmed);
  const stem = (numbered?.[1]?.trim() || trimmed).trim();
  if (!stem) {
    return trimmed;
  }
  const taken = new Set(
    [...existingNames].map((name) => name.trim()).filter(Boolean),
  );
  if (!taken.has(stem)) {
    return stem;
  }
  let index = 2;
  while (taken.has(`${stem} ${index}`)) {
    index += 1;
  }
  return `${stem} ${index}`;
}

export function createEmptyPrintCardDesign(
  page: PrintCardDesignPage,
  options?: {
    layoutKey?: string | null;
    layoutFrontVariantKey?: string | null;
    layoutBackVariantKey?: string | null;
    pageSizePresetId?: string;
    orientation?: PrintCardOrientation;
  },
): PrintCardDesignDocument {
  const pageSizePresetId = options?.pageSizePresetId?.trim() || undefined;
  const doc: PrintCardDesignDocument = {
    designVersion: 4,
    layoutKey: options?.layoutKey ?? null,
    layoutFrontVariantKey: options?.layoutFrontVariantKey ?? null,
    layoutBackVariantKey: options?.layoutBackVariantKey ?? null,
    orientation: options?.orientation === "portrait" ? "portrait" : "landscape",
    page: { ...page },
    front: {
      backgroundColor: "#FFFFFF",
      backgroundImageUrl: null,
      elements: [],
    },
    back: {
      backgroundColor: "#FFFFFF",
      backgroundImageUrl: null,
      elements: [],
    },
    activePageSizePresetId: pageSizePresetId,
    sizeLayouts: {},
  };
  if (pageSizePresetId) {
    doc.sizeLayouts = {
      [pageSizePresetId]: capturePrintCardSizeLayout(doc, page),
    };
  }
  return doc;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function sidePositionsFromElements(
  elements: PrintCardElement[],
): Record<string, PrintCardElementPosition> {
  const positions: Record<string, PrintCardElementPosition> = {};
  for (const el of elements) {
    positions[el.id] = { xCm: el.xCm, yCm: el.yCm };
  }
  return positions;
}

function captureSideSizeLayout(
  side: PrintCardSideDesign,
): PrintCardSizeSideLayout {
  return {
    backgroundColor: side.backgroundColor,
    backgroundImageUrl: side.backgroundImageUrl ?? null,
    positions: sidePositionsFromElements(side.elements),
  };
}

export function capturePrintCardSizeLayout(
  doc: PrintCardDesignDocument,
  page: PrintCardDesignPage = doc.page,
): PrintCardSizeLayout {
  return {
    page: { ...page },
    front: captureSideSizeLayout(doc.front),
    back: captureSideSizeLayout(doc.back),
  };
}

function applyPositionsToSide(
  side: PrintCardSideDesign,
  layout: PrintCardSizeSideLayout,
  page: PrintCardDesignPage,
): PrintCardSideDesign {
  return {
    backgroundColor: layout.backgroundColor,
    backgroundImageUrl: layout.backgroundImageUrl ?? null,
    elements: side.elements.map((el) => {
      const pos = layout.positions[el.id];
      if (!pos) {
        return el;
      }
      const clamped = clampPrintCardElementBox(
        {
          xCm: pos.xCm,
          yCm: pos.yCm,
          widthCm: el.widthCm,
          heightCm: el.heightCm,
          rotationDeg: el.rotationDeg,
        },
        page,
        el.type === "qr" || el.type === "nfc",
      );
      return {
        ...el,
        xCm: clamped.xCm,
        yCm: clamped.yCm,
        widthCm: clamped.widthCm,
        heightCm: clamped.heightCm,
      };
    }),
  };
}

export function mergePrintCardSizeLayout(
  doc: PrintCardDesignDocument,
  pageSizePresetId?: string | null,
): PrintCardDesignDocument {
  const presetId =
    pageSizePresetId?.trim() || doc.activePageSizePresetId?.trim();
  if (!presetId) {
    return cloneJson(doc);
  }
  const layout = doc.sizeLayouts?.[presetId];
  if (!layout) {
    return cloneJson(doc);
  }
  const next = cloneJson(doc);
  next.page = { ...layout.page };
  next.activePageSizePresetId = presetId;
  next.front = applyPositionsToSide(next.front, layout.front, layout.page);
  next.back = applyPositionsToSide(next.back, layout.back, layout.page);
  return next;
}

function migratePrintCardSideElements(
  side: PrintCardSideDesign,
): PrintCardSideDesign {
  return {
    ...side,
    elements: side.elements.map((el) => {
      if (el.type === "shape" && el.reversed) {
        const { reversed: _reversed, ...shape } = el;
        return shape;
      }
      if (el.type === "text" && el.variable === "contact") {
        return { ...el, variable: "email" };
      }
      return el;
    }),
  };
}

export function migratePrintCardDesignDocument(
  doc: PrintCardDesignDocument,
  pageSizePresetId?: string | null,
): PrintCardDesignDocument {
  const next = cloneJson(doc);
  const presetId =
    pageSizePresetId?.trim() ||
    next.activePageSizePresetId?.trim() ||
    undefined;
  if (next.designVersion !== 4) {
    next.designVersion = 4;
  }
  if (next.orientation !== "portrait") {
    next.orientation = "landscape";
  }
  if (!next.sizeLayouts) {
    next.sizeLayouts = {};
  }
  next.front = migratePrintCardSideElements(next.front);
  next.back = migratePrintCardSideElements(next.back);
  if (presetId) {
    next.activePageSizePresetId = presetId;
    if (!next.sizeLayouts[presetId]) {
      next.sizeLayouts[presetId] = capturePrintCardSizeLayout(next);
    }
  }
  return next;
}

export function syncPrintCardActiveSizeLayout(
  doc: PrintCardDesignDocument,
  pageSizePresetId?: string | null,
): PrintCardDesignDocument {
  const next = migratePrintCardDesignDocument(doc, pageSizePresetId);
  const presetId = next.activePageSizePresetId?.trim();
  if (!presetId) {
    return next;
  }
  next.sizeLayouts = {
    ...(next.sizeLayouts ?? {}),
    [presetId]: capturePrintCardSizeLayout(next),
  };
  return next;
}

export function ensurePrintCardSizeLayouts(
  doc: PrintCardDesignDocument,
  presets: Array<{
    id: string;
    widthCm: number;
    heightCm: number;
    bleedMm: number;
    safeZoneInsetMm: number;
  }>,
  activePageSizePresetId?: string | null,
): PrintCardDesignDocument {
  const next = syncPrintCardActiveSizeLayout(doc, activePageSizePresetId);
  const allowedIds = new Set(presets.map((item) => item.id));
  const sizeLayouts: Record<string, PrintCardSizeLayout> = {};
  const sourceId =
    next.activePageSizePresetId &&
    next.sizeLayouts?.[next.activePageSizePresetId]
      ? next.activePageSizePresetId
      : Object.keys(next.sizeLayouts ?? {})[0];
  const sourceLayout =
    (sourceId ? next.sizeLayouts?.[sourceId] : null) ??
    capturePrintCardSizeLayout(next);
  const portrait = next.orientation === "portrait";
  next.orientation = portrait ? "portrait" : "landscape";

  for (const preset of presets) {
    const existing = next.sizeLayouts?.[preset.id];
    const page: PrintCardDesignPage = {
      widthCm: portrait ? preset.heightCm : preset.widthCm,
      heightCm: portrait ? preset.widthCm : preset.heightCm,
      bleedMm: preset.bleedMm,
      safeZoneInsetMm: preset.safeZoneInsetMm,
    };
    if (existing) {
      sizeLayouts[preset.id] = {
        ...existing,
        page,
      };
      continue;
    }
    const cloned = cloneJson(sourceLayout);
    cloned.page = page;
    const clampSide = (
      side: PrintCardSizeSideLayout,
    ): PrintCardSizeSideLayout => {
      const positions: Record<string, PrintCardElementPosition> = {};
      for (const el of [...next.front.elements, ...next.back.elements]) {
        const pos = side.positions[el.id] ?? { xCm: el.xCm, yCm: el.yCm };
        const clamped = clampPrintCardElementBox(
          {
            xCm: pos.xCm,
            yCm: pos.yCm,
            widthCm: el.widthCm,
            heightCm: el.heightCm,
            rotationDeg: el.rotationDeg,
          },
          page,
          el.type === "qr" || el.type === "nfc",
        );
        positions[el.id] = { xCm: clamped.xCm, yCm: clamped.yCm };
      }
      return {
        backgroundColor: side.backgroundColor,
        backgroundImageUrl: side.backgroundImageUrl ?? null,
        positions,
      };
    };
    sizeLayouts[preset.id] = {
      page: cloned.page,
      front: clampSide(cloned.front),
      back: clampSide(cloned.back),
    };
  }

  next.sizeLayouts = sizeLayouts;
  if (
    !next.activePageSizePresetId ||
    !allowedIds.has(next.activePageSizePresetId)
  ) {
    next.activePageSizePresetId = presets[0]?.id ?? next.activePageSizePresetId;
  }
  if (next.activePageSizePresetId && sizeLayouts[next.activePageSizePresetId]) {
    const merged = mergePrintCardSizeLayout(
      { ...next, sizeLayouts },
      next.activePageSizePresetId,
    );
    merged.sizeLayouts = sizeLayouts;
    return merged;
  }
  return next;
}

export function resetPrintCardActiveSizeLayoutPositions(
  doc: PrintCardDesignDocument,
  presets: Array<{
    id: string;
    widthCm: number;
    heightCm: number;
    bleedMm: number;
    safeZoneInsetMm: number;
  }>,
  pageSizePresetId?: string | null,
): PrintCardDesignDocument {
  const synced = syncPrintCardActiveSizeLayout(doc, pageSizePresetId);
  const activeId = synced.activePageSizePresetId?.trim();
  if (!activeId) {
    return synced;
  }
  const layouts = { ...(synced.sizeLayouts ?? {}) };
  const source = layouts[activeId] ?? capturePrintCardSizeLayout(synced);
  layouts[activeId] = source;
  const positionsFromActive = (
    side: PrintCardSizeSideLayout,
    elements: PrintCardElement[],
    page: PrintCardDesignPage,
  ): Record<string, PrintCardElementPosition> => {
    const positions: Record<string, PrintCardElementPosition> = {};
    for (const el of elements) {
      const pos = side.positions[el.id] ?? { xCm: el.xCm, yCm: el.yCm };
      const clamped = clampPrintCardElementBox(
        {
          xCm: pos.xCm,
          yCm: pos.yCm,
          widthCm: el.widthCm,
          heightCm: el.heightCm,
          rotationDeg: el.rotationDeg,
        },
        page,
        el.type === "qr" || el.type === "nfc",
      );
      positions[el.id] = { xCm: clamped.xCm, yCm: clamped.yCm };
    }
    return positions;
  };
  const portrait = synced.orientation === "portrait";
  for (const preset of presets) {
    if (preset.id === activeId) {
      continue;
    }
    const existing = layouts[preset.id];
    const page: PrintCardDesignPage = {
      widthCm: portrait ? preset.heightCm : preset.widthCm,
      heightCm: portrait ? preset.widthCm : preset.heightCm,
      bleedMm: preset.bleedMm,
      safeZoneInsetMm: preset.safeZoneInsetMm,
    };
    layouts[preset.id] = {
      page,
      front: {
        backgroundColor:
          existing?.front.backgroundColor ?? source.front.backgroundColor,
        backgroundImageUrl:
          existing?.front.backgroundImageUrl ??
          source.front.backgroundImageUrl ??
          null,
        positions: positionsFromActive(
          source.front,
          synced.front.elements,
          page,
        ),
      },
      back: {
        backgroundColor:
          existing?.back.backgroundColor ?? source.back.backgroundColor,
        backgroundImageUrl:
          existing?.back.backgroundImageUrl ??
          source.back.backgroundImageUrl ??
          null,
        positions: positionsFromActive(source.back, synced.back.elements, page),
      },
    };
  }
  return mergePrintCardSizeLayout(
    {
      ...synced,
      orientation: portrait ? "portrait" : "landscape",
      sizeLayouts: layouts,
    },
    activeId,
  );
}

export function updatePrintCardElementPositionsInSizeLayouts(
  doc: PrintCardDesignDocument,
  elementId: string,
  position: PrintCardElementPosition,
  side: "front" | "back",
): PrintCardDesignDocument {
  const next = syncPrintCardActiveSizeLayout(doc);
  const activeId = next.activePageSizePresetId?.trim();
  if (!activeId || !next.sizeLayouts?.[activeId]) {
    return next;
  }
  const layout = next.sizeLayouts[activeId];
  next.sizeLayouts = {
    ...next.sizeLayouts,
    [activeId]: {
      ...layout,
      [side]: {
        ...layout[side],
        positions: {
          ...layout[side].positions,
          [elementId]: { xCm: position.xCm, yCm: position.yCm },
        },
      },
    },
  };
  return next;
}

export function addPrintCardElementPositionToAllSizeLayouts(
  doc: PrintCardDesignDocument,
  element: PrintCardElement,
  side: "front" | "back",
): PrintCardDesignDocument {
  const next = syncPrintCardActiveSizeLayout(doc);
  const layouts = { ...(next.sizeLayouts ?? {}) };
  for (const [presetId, layout] of Object.entries(layouts)) {
    const clamped = clampPrintCardElementBox(
      {
        xCm: element.xCm,
        yCm: element.yCm,
        widthCm: element.widthCm,
        heightCm: element.heightCm,
        rotationDeg: element.rotationDeg,
      },
      layout.page,
      element.type === "qr" || element.type === "nfc",
    );
    layouts[presetId] = {
      ...layout,
      [side]: {
        ...layout[side],
        positions: {
          ...layout[side].positions,
          [element.id]: { xCm: clamped.xCm, yCm: clamped.yCm },
        },
      },
    };
  }
  next.sizeLayouts = layouts;
  return next;
}

export function removePrintCardElementFromSizeLayouts(
  doc: PrintCardDesignDocument,
  elementId: string,
): PrintCardDesignDocument {
  const next = syncPrintCardActiveSizeLayout(doc);
  const layouts = { ...(next.sizeLayouts ?? {}) };
  for (const [presetId, layout] of Object.entries(layouts)) {
    const frontPositions = { ...layout.front.positions };
    const backPositions = { ...layout.back.positions };
    delete frontPositions[elementId];
    delete backPositions[elementId];
    layouts[presetId] = {
      ...layout,
      front: { ...layout.front, positions: frontPositions },
      back: { ...layout.back, positions: backPositions },
    };
  }
  next.sizeLayouts = layouts;
  return next;
}

export function printCardContentHasNfcSlot(content: string): boolean {
  return (
    /\$\{\s*nfcIcon\s*\}/.test(content) ||
    /\bdata-pc-type\s*=\s*["']nfc["']/.test(content) ||
    /\bpc-(?:card__)?nfc\b/.test(content) ||
    /\bpc-back__nfc\b/.test(content)
  );
}

export function compilePrintCardDesign(
  doc: PrintCardDesignDocument,
  pageSizePresetId?: string | null,
): PrintCardCompiledSizeContent {
  const merged = mergePrintCardSizeLayout(doc, pageSizePresetId);
  return {
    frontContent: compilePrintCardSide("front", merged.front),
    backContent: compilePrintCardSide("back", merged.back),
    frontBackgroundColor: merged.front.backgroundColor,
    backBackgroundColor: merged.back.backgroundColor,
    frontBackgroundImageUrl: merged.front.backgroundImageUrl ?? null,
    backBackgroundImageUrl: merged.back.backgroundImageUrl ?? null,
  };
}

export function compilePrintCardDesignForPageSizes(
  doc: PrintCardDesignDocument,
  pageSizePresetIds: string[],
): Record<string, PrintCardCompiledSizeContent> {
  const synced = syncPrintCardActiveSizeLayout(doc);
  const result: Record<string, PrintCardCompiledSizeContent> = {};
  for (const presetId of pageSizePresetIds) {
    const id = presetId.trim();
    if (!id) {
      continue;
    }
    result[id] = compilePrintCardDesign(synced, id);
  }
  return result;
}

export function printCardDesignFromLayoutHtml(input: {
  frontContent: string;
  backContent: string;
  page: PrintCardDesignPage;
  layoutKey?: string | null;
  layoutFrontVariantKey?: string | null;
  layoutBackVariantKey?: string | null;
  pageSizePresetId?: string | null;
  orientation?: PrintCardOrientation;
  frontBackgroundColor?: string;
  backBackgroundColor?: string;
  frontBackgroundImageUrl?: string | null;
  backBackgroundImageUrl?: string | null;
}): PrintCardDesignDocument {
  const pageSizePresetId = input.pageSizePresetId?.trim() || undefined;
  const doc = createEmptyPrintCardDesign(input.page, {
    layoutKey: input.layoutKey ?? null,
    layoutFrontVariantKey: input.layoutFrontVariantKey ?? null,
    layoutBackVariantKey: input.layoutBackVariantKey ?? null,
    pageSizePresetId,
    orientation: input.orientation,
  });
  doc.front = designSideFromLayoutHtml(input.frontContent, input.page, "front");
  doc.back = designSideFromLayoutHtml(input.backContent, input.page, "back");
  if (input.frontBackgroundColor) {
    doc.front.backgroundColor = input.frontBackgroundColor;
  }
  if (input.backBackgroundColor) {
    doc.back.backgroundColor = input.backBackgroundColor;
  }
  if (input.frontBackgroundImageUrl !== undefined) {
    doc.front.backgroundImageUrl = input.frontBackgroundImageUrl;
  }
  if (input.backBackgroundImageUrl !== undefined) {
    doc.back.backgroundImageUrl = input.backBackgroundImageUrl;
  }
  doc.front.elements = doc.front.elements.map((el) => {
    const name = el.name?.trim() || printCardElementDefaultName(el);
    return name === el.name ? el : { ...el, name };
  });
  doc.back.elements = doc.back.elements.map((el) => {
    const name = el.name?.trim() || printCardElementDefaultName(el);
    const next = name === el.name ? el : { ...el, name };
    return next.type === "logo" && next.align == null
      ? { ...next, align: "center" as const }
      : next;
  });
  return syncPrintCardActiveSizeLayout(doc, pageSizePresetId);
}

function newDesignElementId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `el_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function escapeDesignHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function designElementSafeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, "") || "el";
}

function designElementSelector(id: string): string {
  return `.pc-el-${designElementSafeId(id)}`;
}

function compilePrintCardSide(
  side: "front" | "back",
  design: PrintCardSideDesign,
): string {
  const sorted = [...design.elements].sort((a, b) => a.zIndex - b.zIndex);
  const cssParts: string[] = [];
  const bodyParts: string[] = [];
  for (const el of sorted) {
    const selector = designElementSelector(el.id);
    const classParts = [`pc-el`, `pc-el-${designElementSafeId(el.id)}`];
    if (el.type === "qr") {
      classParts.push("pc-card__qr");
    } else if (el.type === "nfc") {
      classParts.push("pc-card__nfc");
    }
    const className = classParts.join(" ");
    const decls = [
      "position: absolute;",
      "box-sizing: border-box;",
      `left: ${el.xCm}cm;`,
      `top: ${el.yCm}cm;`,
      `width: ${el.widthCm}cm;`,
      `height: ${el.heightCm}cm;`,
      `max-width: ${el.widthCm}cm;`,
      `max-height: ${el.heightCm}cm;`,
      `z-index: ${el.zIndex};`,
    ];
    let rotationDeg = 0;
    if (typeof el.rotationDeg === "number" && Number.isFinite(el.rotationDeg)) {
      rotationDeg = el.rotationDeg % 360;
      if (rotationDeg > 180) {
        rotationDeg -= 360;
      } else if (rotationDeg <= -180) {
        rotationDeg += 360;
      }
      rotationDeg = Math.round(rotationDeg * 10) / 10;
    }
    const reversed = el.type !== "shape" && el.reversed === true;
    const transformParts: string[] = [];
    if (rotationDeg !== 0) {
      transformParts.push(`rotate(${rotationDeg}deg)`);
    }
    if (reversed) {
      transformParts.push("scaleX(-1)");
    }
    if (transformParts.length > 0) {
      decls.push(
        `transform: ${transformParts.join(" ")};`,
        "transform-origin: center center;",
      );
    }
    if (el.type === "text") {
      const squashParts = printCardTextElementSquashParts(el);
      if (squashParts) {
        const squashSizes = [
          ...squashParts.map((part) => part.fontSizePt),
          el.squashSeparatorStyle?.fontSizePt,
        ].filter(
          (size): size is number => typeof size === "number" && size > 0,
        );
        decls.push(
          `font-size: ${
            squashSizes.length > 0
              ? Math.max(...squashSizes)
              : typeof el.fontSizePt === "number" && el.fontSizePt > 0
                ? el.fontSizePt
                : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT
          }pt;`,
        );
      } else {
        if (typeof el.fontSizePt === "number") {
          decls.push(`font-size: ${el.fontSizePt}pt;`);
        }
        if (typeof el.fontWeight === "number") {
          decls.push(`font-weight: ${el.fontWeight};`);
        }
        if (el.italic) {
          decls.push("font-style: italic;");
        }
        if (el.uppercase) {
          decls.push("text-transform: uppercase;");
        }
        if (el.color) {
          decls.push(`color: ${el.color};`);
        }
      }
      if (el.align) {
        decls.push(`text-align: ${el.align};`);
      }
      const textValign =
        el.valign === "middle" || el.valign === "bottom" ? el.valign : "top";
      const textJustify =
        textValign === "middle"
          ? "center"
          : textValign === "bottom"
            ? "flex-end"
            : "flex-start";
      const squashLineHeights = squashParts
        ? [
            ...squashParts.map((part) => part.lineHeight),
            el.squashSeparatorStyle?.lineHeight,
          ].filter(
            (value): value is number => typeof value === "number" && value >= 0,
          )
        : [];
      const lineHeight =
        squashLineHeights.length > 0
          ? Math.max(...squashLineHeights)
          : typeof el.lineHeight === "number" && el.lineHeight >= 0
            ? el.lineHeight
            : 1;
      const textAlign =
        el.align === "right" || el.align === "center" ? el.align : "left";
      const inkWidth = `calc(${el.widthCm}cm + 0.08em)`;
      decls.push(
        "display: flex;",
        "flex-direction: column;",
        `justify-content: ${textJustify};`,
        "max-height: 100%;",
        "min-height: 0;",
        `width: ${inkWidth};`,
        `max-width: ${inkWidth};`,
        textAlign === "right"
          ? "margin: 0;"
          : textAlign === "center"
            ? "margin: 0 0 0 -0.04em;"
            : "margin: 0 0 0 -0.08em;",
        "padding: 0;",
        "border: 0;",
        `line-height: ${lineHeight};`,
        "box-sizing: border-box;",
      );
      const maxLines =
        typeof el.maxLines === "number" && el.maxLines >= 1
          ? Math.min(20, Math.floor(el.maxLines))
          : 0;
      if (maxLines === 1) {
        decls.push("white-space: nowrap;", "overflow: hidden;");
      } else if (maxLines > 1) {
        decls.push(
          "white-space: normal;",
          "overflow-wrap: anywhere;",
          "word-break: normal;",
          "overflow: hidden;",
          "text-overflow: clip;",
          `max-height: ${Math.round(maxLines * lineHeight * 1000) / 1000}em;`,
        );
      } else if (squashParts) {
        decls.push(
          "white-space: normal;",
          "overflow-wrap: anywhere;",
          "word-break: normal;",
          "overflow: hidden;",
        );
      } else {
        decls.push(
          "white-space: normal;",
          "overflow-wrap: anywhere;",
          "word-break: normal;",
          "overflow-x: visible;",
          "overflow-y: hidden;",
        );
      }
    }
    if (el.type === "logo" || el.type === "image") {
      const align =
        el.align === "center" || el.align === "right" || el.align === "left"
          ? el.align
          : el.type === "logo" && side === "back"
            ? "center"
            : "left";
      const valign =
        el.valign === "top" || el.valign === "bottom" || el.valign === "middle"
          ? el.valign
          : "middle";
      const objectX =
        align === "center" ? "center" : align === "right" ? "right" : "left";
      const objectY =
        valign === "top" ? "top" : valign === "bottom" ? "bottom" : "center";
      decls.push(
        "display: block;",
        "object-fit: contain;",
        `object-position: ${objectX} ${objectY};`,
      );
    }
    if (el.type === "qr") {
      decls.push("overflow: hidden;");
    }
    if (el.type === "nfc") {
      decls.push("overflow: hidden;");
      if (el.color) {
        decls.push(`color: ${el.color};`);
      }
    }
    if (el.type === "shape") {
      if (el.fill) {
        decls.push(`background-color: ${el.fill};`);
      }
      if (
        typeof el.borderRadiusMm === "number" &&
        Number.isFinite(el.borderRadiusMm) &&
        el.borderRadiusMm > 0
      ) {
        const radiusMm = Math.min(50, Math.max(0, el.borderRadiusMm));
        decls.push(`border-radius: ${Math.round(radiusMm * 10) / 10}mm;`);
      }
    }
    cssParts.push(`${selector} {\n  ${decls.join("\n  ")}\n}`);
    if (el.type === "qr") {
      cssParts.push(
        `${selector} > img {\n  display: block;\n  width: 100%;\n  height: 100%;\n  object-fit: contain;\n  object-position: top left;\n}`,
      );
    }
    if (el.type === "nfc") {
      cssParts.push(
        `${selector} svg,\n${selector} svg g,\n${selector} svg path {\n  fill: currentColor;\n}`,
      );
      cssParts.push(
        `${selector} svg {\n  display: block;\n  width: 100%;\n  height: 100%;\n}`,
      );
    }
    const layerName = el.name?.trim() || printCardElementDefaultName(el);
    let textFitAttr = "";
    if (el.type === "text") {
      const fit = el.textFit === "shrink" ? "shrink" : "none";
      textFitAttr = ` data-pc-fit="${fit}"`;
      const maxLines =
        typeof el.maxLines === "number" && el.maxLines >= 1
          ? Math.min(20, Math.floor(el.maxLines))
          : 0;
      if (maxLines >= 1) {
        textFitAttr += ` data-pc-max-lines="${maxLines}"`;
      }
      if (fit === "shrink") {
        const basePt =
          typeof el.fontSizePt === "number" && el.fontSizePt >= 0
            ? el.fontSizePt
            : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
        const rawMin =
          typeof el.minFontSizePt === "number" && el.minFontSizePt >= 0
            ? el.minFontSizePt
            : Math.min(basePt, PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT);
        textFitAttr += ` data-pc-min-pt="${Math.min(basePt, Math.round(rawMin * 10) / 10)}"`;
      }
      const textValign =
        el.valign === "middle" || el.valign === "bottom" ? el.valign : "top";
      if (textValign !== "top") {
        textFitAttr += ` data-pc-valign="${textValign}"`;
      }
    } else if (
      (el.type === "logo" || el.type === "image") &&
      (el.valign === "top" || el.valign === "bottom")
    ) {
      textFitAttr = ` data-pc-valign="${el.valign}"`;
    }
    const rotationAttr =
      rotationDeg !== 0 ? ` data-pc-rotation="${rotationDeg}"` : "";
    const reverseAttr = reversed ? ' data-pc-reverse="1"' : "";
    const attrs = `class="${className}" data-pc-id="${escapeDesignHtml(el.id)}" data-pc-type="${el.type}"${textFitAttr}${rotationAttr}${reverseAttr} data-pc-name="${escapeDesignHtml(layerName)}"`;
    if (el.type === "logo") {
      const logoSrc = el.srcUrl?.trim()
        ? escapeDesignHtml(el.srcUrl.trim())
        : "${companyImageUrl}";
      bodyParts.push(`<img ${attrs} src="${logoSrc}" alt="" />`);
    } else if (el.type === "image") {
      bodyParts.push(
        `<img ${attrs} src="${el.srcUrl ? escapeDesignHtml(el.srcUrl) : ""}" alt="" />`,
      );
    } else if (el.type === "qr") {
      bodyParts.push(
        `<div ${attrs}><img src="\${QRdigitalCardUrl}" alt="" /></div>`,
      );
    } else if (el.type === "nfc") {
      bodyParts.push(`<div ${attrs}>\${nfcIcon}</div>`);
    } else if (el.type === "shape") {
      bodyParts.push(`<div ${attrs}></div>`);
    } else {
      const squashParts = printCardTextElementSquashParts(el);
      const variableKey = el.variable?.trim() || "";
      let varAttr = !variableKey
        ? ` data-pc-var=""`
        : variableKey !== "custom"
          ? ` data-pc-var="${escapeDesignHtml(variableKey)}"`
          : "";
      let inner: string;
      if (squashParts) {
        const separator =
          typeof el.squashSeparator === "string"
            ? el.squashSeparator
            : PRINT_CARD_DEFAULT_SQUASH_SEPARATOR;
        const squashSizes = [
          ...squashParts.map((part) => part.fontSizePt),
          el.squashSeparatorStyle?.fontSizePt,
        ].filter(
          (size): size is number => typeof size === "number" && size > 0,
        );
        const baseFontSizePt =
          squashSizes.length > 0
            ? Math.max(...squashSizes)
            : typeof el.fontSizePt === "number" && el.fontSizePt > 0
              ? el.fontSizePt
              : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
        const partsCsv = squashParts
          .map((part) => escapeDesignHtml(part.variable))
          .join(",");
        varAttr = ` data-pc-var="squash" data-pc-squash-parts="${partsCsv}" data-pc-squash-sep="${escapeDesignHtml(separator)}"`;
        inner = compilePrintCardSquashInner(
          squashParts,
          separator,
          baseFontSizePt,
          el.squashSeparatorStyle,
        );
      } else {
        inner = compilePrintCardVariableInner(el.variable, el.text);
      }
      const tag = el.variable === "tagline" ? "p" : "div";
      const hasLineSpans = inner.includes('class="pc-text-line"');
      const wrappedInner = hasLineSpans
        ? inner
        : `<span class="pc-text-flow">${inner}</span>`;
      bodyParts.push(`<${tag} ${attrs}${varAttr}>${wrappedInner}</${tag}>`);
    }
  }
  const needsTextFlowCss = bodyParts.some((part) =>
    part.includes('class="pc-text-flow"'),
  );
  const needsTextLineCss = bodyParts.some((part) =>
    part.includes('class="pc-text-line"'),
  );
  if (needsTextLineCss) {
    cssParts.unshift(
      ".pc-text-line{display:block;width:100%;min-width:0;max-width:100%;}",
    );
  }
  if (needsTextFlowCss) {
    cssParts.unshift(
      ".pc-text-flow{display:block;width:100%;min-width:0;max-width:100%;}",
    );
  }
  const css = cssParts.join("\n");
  const style = css ? `<style>\n${css}\n</style>\n` : "";
  const rootClass = side === "front" ? "pc-front" : "pc-back";
  return `${style}<div class="container">\n  <div class="${rootClass}">\n    ${bodyParts.join("\n    ")}\n  </div>\n</div>`;
}

function roundLayoutCm(value: number): number {
  return Math.round(value * 100) / 100;
}

function parseLayoutCmValue(value: string | undefined): number | null {
  if (!value) {
    return null;
  }
  const trimmed = value.trim();
  const direct = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*cm$/i);
  if (direct) {
    const n = Number.parseFloat(direct[1]);
    return Number.isFinite(n) ? n : null;
  }
  const inkFlush = trimmed.match(
    /^calc\(\s*(-?\d+(?:\.\d+)?)\s*cm\s*\+\s*0\.08em\s*\)$/i,
  );
  if (inkFlush) {
    const n = Number.parseFloat(inkFlush[1]);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseLayoutPercent(value: string | undefined): number | null {
  if (!value) {
    return null;
  }
  const match = value.trim().match(/^(-?\d+(?:\.\d+)?)\s*%$/);
  if (!match) {
    return null;
  }
  const n = Number.parseFloat(match[1]);
  return Number.isFinite(n) ? n : null;
}

function parseLayoutMaxWidthCm(
  value: string | undefined,
  pageWidthCm: number,
): number | null {
  if (!value) {
    return null;
  }
  const direct = parseLayoutCmValue(value);
  if (direct != null) {
    return direct;
  }
  const calc = value
    .trim()
    .match(/^calc\(\s*100%\s*-\s*([\d.]+)cm\s*-\s*([\d.]+)cm\s*\)$/i);
  if (!calc) {
    return null;
  }
  return pageWidthCm - Number.parseFloat(calc[1]) - Number.parseFloat(calc[2]);
}

function parseLayoutCssMap(css: string): Map<string, Record<string, string>> {
  const cssMap = new Map<string, Record<string, string>>();
  const ruleRe = /([^{}]+)\{([^}]*)\}/g;
  let match: RegExpExecArray | null;
  while ((match = ruleRe.exec(css))) {
    const selectors = match[1]
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const decls: Record<string, string> = {};
    for (const part of match[2].split(";")) {
      const idx = part.indexOf(":");
      if (idx < 0) {
        continue;
      }
      const key = part.slice(0, idx).trim().toLowerCase();
      const val = part.slice(idx + 1).trim();
      if (key) {
        decls[key] = val;
      }
    }
    for (const selector of selectors) {
      cssMap.set(selector, { ...(cssMap.get(selector) ?? {}), ...decls });
      const simple = selector.split(/\s+/).pop() ?? selector;
      if (simple !== selector) {
        cssMap.set(simple, { ...(cssMap.get(simple) ?? {}), ...decls });
      }
    }
  }
  return cssMap;
}

function layoutDecls(
  cssMap: Map<string, Record<string, string>>,
  ...selectors: string[]
): Record<string, string> | undefined {
  for (const selector of selectors) {
    const found = cssMap.get(selector);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function layoutMarginTopCm(
  cssMap: Map<string, Record<string, string>>,
  selector: string,
  fallback: number,
): number {
  return (
    parseLayoutCmValue(layoutDecls(cssMap, selector)?.["margin-top"]) ??
    fallback
  );
}

type LayoutBox = {
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
};

function layoutAbsBox(
  decls: Record<string, string> | undefined,
  page: PrintCardDesignPage,
  defaults: { widthCm: number; heightCm: number },
): LayoutBox {
  const left = parseLayoutCmValue(decls?.["left"]);
  const right = parseLayoutCmValue(decls?.["right"]);
  const top = parseLayoutCmValue(decls?.["top"]);
  const bottom = parseLayoutCmValue(decls?.["bottom"]);
  const topPct = parseLayoutPercent(decls?.["top"]);
  const leftPct = parseLayoutPercent(decls?.["left"]);
  const transform = decls?.["transform"] ?? "";
  const translateCenter = /translate\s*\(\s*-50%\s*,\s*-50%\s*\)/i.test(
    transform,
  );
  const translateY50 = /translateY\s*\(\s*-50%\s*\)/i.test(transform);

  let width =
    parseLayoutCmValue(decls?.["width"]) ??
    parseLayoutMaxWidthCm(decls?.["max-width"], page.widthCm);
  let height =
    parseLayoutCmValue(decls?.["height"]) ??
    parseLayoutCmValue(decls?.["max-height"]);

  if (width == null && left != null && right != null) {
    width = page.widthCm - left - right;
  }
  if (height == null && top != null && bottom != null) {
    height = page.heightCm - top - bottom;
  }
  width = width ?? defaults.widthCm;
  height = height ?? defaults.heightCm;

  let xCm: number;
  if (leftPct != null && translateCenter) {
    xCm = (page.widthCm * leftPct) / 100 - width / 2;
  } else if (left != null) {
    xCm = left;
  } else if (right != null) {
    xCm = page.widthCm - right - width;
  } else if (leftPct != null) {
    xCm = (page.widthCm * leftPct) / 100;
  } else {
    xCm = 0;
  }

  let yCm: number;
  if (topPct != null && (translateY50 || translateCenter)) {
    yCm = (page.heightCm * topPct) / 100 - height / 2;
  } else if (top != null) {
    yCm = top;
  } else if (bottom != null) {
    yCm = page.heightCm - bottom - height;
  } else if (topPct != null) {
    yCm = (page.heightCm * topPct) / 100;
  } else {
    yCm = 0;
  }

  return {
    xCm: roundLayoutCm(xCm),
    yCm: roundLayoutCm(yCm),
    widthCm: roundLayoutCm(Math.max(PRINT_CARD_MIN_SIZE_CM, width)),
    heightCm: roundLayoutCm(Math.max(PRINT_CARD_MIN_SIZE_CM, height)),
  };
}

function bodyHasClass(body: string, classToken: string): boolean {
  return new RegExp(`class=["'][^"']*\\b${classToken}\\b`, "i").test(body);
}

function designSideFromCompiledHtml(
  html: string,
  page: PrintCardDesignPage,
): PrintCardSideDesign | null {
  if (!/\bdata-pc-type\s*=/.test(html)) {
    return null;
  }
  const styleMatch = html.match(/^<style\b[^>]*>([\s\S]*?)<\/style>/i);
  const css = styleMatch?.[1] ?? "";
  const body = styleMatch ? html.slice(styleMatch[0].length) : html;
  const cssMap = parseLayoutCssMap(css);
  const elements: PrintCardElement[] = [];
  const tagRe =
    /<(img|div|p)\b([^>]*\bdata-pc-type\s*=\s*["']([^"']+)["'][^>]*)>(?:([\s\S]*?)<\/\1>)?/gi;
  let match: RegExpExecArray | null;
  let z = 1;
  while ((match = tagRe.exec(body))) {
    const attrs = match[2] ?? "";
    const typeRaw = (match[3] ?? "").trim().toLowerCase();
    const idMatch = attrs.match(/\bdata-pc-id\s*=\s*["']([^"']+)["']/i);
    const varMatch = attrs.match(/\bdata-pc-var\s*=\s*["']([^"']*)["']/i);
    const rotationMatch = attrs.match(
      /\bdata-pc-rotation\s*=\s*["']([^"']+)["']/i,
    );
    const id = idMatch?.[1]?.trim() || newDesignElementId();
    const decls = layoutDecls(cssMap, designElementSelector(id)) ?? {};
    const box = layoutAbsBox(decls, page, {
      widthCm: 1,
      heightCm: 1,
    });
    const rotationRaw = Number.parseFloat(
      (rotationMatch?.[1] ?? "").trim() ||
        (decls["transform"] ?? "").match(
          /rotate\s*\(\s*(-?[\d.]+)\s*deg\s*\)/i,
        )?.[1] ||
        "",
    );
    let rotationDeg: number | undefined;
    if (Number.isFinite(rotationRaw)) {
      let deg = rotationRaw % 360;
      if (deg > 180) {
        deg -= 360;
      } else if (deg <= -180) {
        deg += 360;
      }
      deg = Math.round(deg * 10) / 10;
      if (deg !== 0) {
        rotationDeg = deg;
      }
    }
    const nameMatch = attrs.match(/\bdata-pc-name\s*=\s*["']([^"']+)["']/i);
    const layerName = nameMatch?.[1]?.trim() || undefined;
    const reverseAttr = attrs.match(
      /\bdata-pc-reverse\s*=\s*["']([^"']+)["']/i,
    )?.[1];
    const reversed =
      reverseAttr === "1" ||
      reverseAttr === "true" ||
      /scaleX\s*\(\s*-1\s*\)/i.test(decls["transform"] ?? "");
    const withBox = {
      ...box,
      ...(rotationDeg != null ? { rotationDeg } : {}),
      ...(reversed ? { reversed: true as const } : {}),
      ...(layerName ? { name: layerName } : {}),
    };
    if (typeRaw === "logo") {
      const srcMatch = attrs.match(/\bsrc\s*=\s*["']([^"']*)["']/i);
      const src = (srcMatch?.[1] ?? "").trim();
      const usesCompany =
        !src ||
        src === "${companyImageUrl}" ||
        /\$\{\s*companyImageUrl\s*\}/.test(src);
      const objectPos = (decls["object-position"] ?? "").trim().toLowerCase();
      const posParts = objectPos.split(/\s+/).filter(Boolean);
      const xPart = posParts[0] ?? "";
      const yPart = posParts[1] ?? "";
      const align =
        xPart === "right"
          ? ("right" as const)
          : xPart === "center"
            ? ("center" as const)
            : xPart === "left"
              ? ("left" as const)
              : undefined;
      const valignAttr = attrs
        .match(/\bdata-pc-valign\s*=\s*["']([^"']+)["']/i)?.[1]
        ?.trim()
        .toLowerCase();
      const valign =
        valignAttr === "top" ||
        valignAttr === "middle" ||
        valignAttr === "bottom"
          ? valignAttr
          : yPart === "top"
            ? ("top" as const)
            : yPart === "bottom"
              ? ("bottom" as const)
              : yPart === "center"
                ? ("middle" as const)
                : undefined;
      elements.push({
        id,
        type: "logo",
        ...withBox,
        zIndex: z++,
        ...(usesCompany
          ? { srcVariable: "companyImageUrl" as const }
          : { srcUrl: src }),
        ...(align ? { align } : {}),
        ...(valign ? { valign } : {}),
      });
      continue;
    }
    if (typeRaw === "image") {
      const srcMatch = attrs.match(/\bsrc\s*=\s*["']([^"']*)["']/i);
      const objectPos = (decls["object-position"] ?? "").trim().toLowerCase();
      const posParts = objectPos.split(/\s+/).filter(Boolean);
      const xPart = posParts[0] ?? "";
      const yPart = posParts[1] ?? "";
      const align =
        xPart === "right"
          ? ("right" as const)
          : xPart === "center"
            ? ("center" as const)
            : xPart === "left"
              ? ("left" as const)
              : undefined;
      const valignAttr = attrs
        .match(/\bdata-pc-valign\s*=\s*["']([^"']+)["']/i)?.[1]
        ?.trim()
        .toLowerCase();
      const valign =
        valignAttr === "top" ||
        valignAttr === "middle" ||
        valignAttr === "bottom"
          ? valignAttr
          : yPart === "top"
            ? ("top" as const)
            : yPart === "bottom"
              ? ("bottom" as const)
              : yPart === "center"
                ? ("middle" as const)
                : undefined;
      elements.push({
        id,
        type: "image",
        ...withBox,
        zIndex: z++,
        srcUrl: srcMatch?.[1] ?? "",
        ...(align ? { align } : {}),
        ...(valign ? { valign } : {}),
      });
      continue;
    }
    if (typeRaw === "qr") {
      elements.push({
        id,
        type: "qr",
        ...withBox,
        zIndex: z++,
        srcVariable: "QRdigitalCardUrl",
      });
      continue;
    }
    if (typeRaw === "nfc") {
      elements.push({
        id,
        type: "nfc",
        ...withBox,
        zIndex: z++,
        srcVariable: "nfcIcon",
        color: decls["color"],
      });
      continue;
    }
    if (typeRaw === "shape") {
      const radiusRaw = (decls["border-radius"] ?? "").match(
        /([\d.]+)\s*mm/i,
      )?.[1];
      const radiusParsed = radiusRaw != null ? Number(radiusRaw) : NaN;
      const borderRadiusMm =
        Number.isFinite(radiusParsed) && radiusParsed > 0
          ? Math.min(50, Math.max(0, Math.round(radiusParsed * 10) / 10))
          : undefined;
      const { reversed: _shapeReversed, ...shapeBox } = withBox;
      elements.push({
        id,
        type: "shape",
        ...shapeBox,
        zIndex: z++,
        fill: decls["background-color"],
        ...(borderRadiusMm != null ? { borderRadiusMm } : {}),
      });
      continue;
    }
    if (typeRaw === "text") {
      const variableRaw = varMatch ? (varMatch[1] ?? "").trim() : "custom";
      const variable = !varMatch
        ? "custom"
        : !variableRaw
          ? undefined
          : variableRaw === "contact"
            ? "email"
            : variableRaw;
      const fontSizeMatch = decls["font-size"]?.match(/([\d.]+)\s*pt/i);
      const fontWeightMatch = decls["font-weight"]?.match(/(\d+)/);
      const lineHeightMatch = (decls["line-height"] ?? "")
        .trim()
        .toLowerCase()
        .match(/^([\d.]+)(%?)$/);
      const lineHeightParsed = lineHeightMatch
        ? Number.parseFloat(lineHeightMatch[1]) /
          (lineHeightMatch[2] === "%" ? 100 : 1)
        : NaN;
      const lineHeight =
        Number.isFinite(lineHeightParsed) && lineHeightParsed >= 0
          ? Math.round(lineHeightParsed * 100) / 100
          : undefined;
      const alignRaw = (decls["text-align"] ?? "").trim().toLowerCase();
      const align =
        alignRaw === "left" || alignRaw === "center" || alignRaw === "right"
          ? alignRaw
          : undefined;
      const valignAttr = attrs
        .match(/\bdata-pc-valign\s*=\s*["']([^"']+)["']/i)?.[1]
        ?.trim()
        .toLowerCase();
      const justifyRaw = (decls["justify-content"] ?? "").trim().toLowerCase();
      const valign =
        valignAttr === "top" ||
        valignAttr === "middle" ||
        valignAttr === "bottom"
          ? valignAttr
          : justifyRaw === "center"
            ? ("middle" as const)
            : justifyRaw === "flex-end" || justifyRaw === "end"
              ? ("bottom" as const)
              : justifyRaw === "flex-start" || justifyRaw === "start"
                ? ("top" as const)
                : undefined;
      const rawInner = match[4] ?? "";
      const inner = rawInner
        .replace(/<\/span>\s*<span\b[^>]*\bpc-text-line\b[^>]*>/gi, "\n")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&")
        .replace(/\r\n/g, "\n")
        .trim();
      const fitRaw = attrs.match(/\bdata-pc-fit\s*=\s*["']([^"']+)["']/i)?.[1];
      const textFit: PrintCardTextFit | undefined =
        fitRaw === "shrink" ? "shrink" : undefined;
      const maxLinesRaw = Number.parseInt(
        attrs.match(/\bdata-pc-max-lines\s*=\s*["']([^"']+)["']/i)?.[1] ?? "",
        10,
      );
      const maxLines =
        Number.isFinite(maxLinesRaw) && maxLinesRaw >= 1
          ? Math.min(20, maxLinesRaw)
          : undefined;
      const minPtRaw = Number.parseFloat(
        attrs.match(/\bdata-pc-min-pt\s*=\s*["']([^"']+)["']/i)?.[1] ?? "",
      );
      const parsedFontSizePt = fontSizeMatch
        ? Number.parseFloat(fontSizeMatch[1])
        : undefined;
      let minFontSizePt: number | undefined;
      if (textFit === "shrink" && Number.isFinite(minPtRaw) && minPtRaw >= 0) {
        const basePt =
          typeof parsedFontSizePt === "number" && parsedFontSizePt >= 0
            ? parsedFontSizePt
            : minPtRaw;
        minFontSizePt = Math.min(basePt, Math.round(minPtRaw * 10) / 10);
      }
      const squash =
        variable === "squash"
          ? parsePrintCardSquashPartsFromAttrs(attrs, rawInner)
          : null;
      const textVariable = squash ? "squash" : variable;
      elements.push({
        id,
        type: "text",
        ...withBox,
        zIndex: z++,
        ...(textVariable != null ? { variable: textVariable } : {}),
        text:
          textVariable === "tagline" || textVariable === "custom"
            ? inner ||
              (textVariable === "tagline"
                ? PRINT_CARD_DEFAULT_TAGLINE_TEXT
                : undefined)
            : undefined,
        ...(squash
          ? {
              squashParts:
                squash.parts.some((part) => part.lineHeight != null) ||
                squash.separatorStyle?.lineHeight != null ||
                lineHeight == null ||
                lineHeight === 1
                  ? squash.parts
                  : squash.parts.map((part) => ({ ...part, lineHeight })),
              squashSeparator: squash.separator,
              ...(squash.separatorStyle
                ? { squashSeparatorStyle: squash.separatorStyle }
                : {}),
            }
          : {}),
        fontSizePt:
          squash || !fontSizeMatch
            ? undefined
            : Number.parseFloat(fontSizeMatch[1]),
        fontWeight: squash
          ? undefined
          : fontWeightMatch
            ? (Number.parseInt(fontWeightMatch[1], 10) as 400 | 500 | 600 | 700)
            : undefined,
        italic: squash
          ? undefined
          : /italic/i.test(decls["font-style"] ?? "") || undefined,
        uppercase: squash
          ? undefined
          : /uppercase/i.test(decls["text-transform"] ?? "") || undefined,
        align,
        ...(valign ? { valign } : {}),
        color: squash ? undefined : decls["color"],
        ...(textFit ? { textFit } : {}),
        ...(maxLines != null ? { maxLines } : {}),
        ...(minFontSizePt != null ? { minFontSizePt } : {}),
        ...(!squash && lineHeight != null && lineHeight !== 1
          ? { lineHeight }
          : {}),
      });
    }
  }
  if (elements.length === 0) {
    return null;
  }
  return {
    backgroundColor: "#FFFFFF",
    backgroundImageUrl: null,
    elements: elements.map((el) => {
      const name = el.name?.trim() || printCardElementDefaultName(el);
      return name === el.name ? el : { ...el, name };
    }),
  };
}

function designSideFromLayoutHtml(
  html: string,
  page: PrintCardDesignPage,
  side: "front" | "back",
): PrintCardSideDesign {
  const compiled = designSideFromCompiledHtml(html, page);
  if (compiled) {
    return compiled;
  }
  const styleMatch = html.match(/^<style\b[^>]*>([\s\S]*?)<\/style>/i);
  const css = styleMatch?.[1] ?? "";
  const body = styleMatch ? html.slice(styleMatch[0].length) : html;
  const cssMap = parseLayoutCssMap(css);
  const elements: PrintCardElement[] = [];
  let z = 1;

  const pushText = (
    present: boolean,
    variable: PrintCardTextVariable,
    box: LayoutBox,
    opts?: {
      text?: string;
      fontSizePt?: number;
      fontWeight?: 400 | 500 | 600 | 700;
    },
  ): void => {
    if (!present) {
      return;
    }
    const fontSizePt =
      opts?.fontSizePt ??
      (variable === "userName"
        ? PRINT_CARD_DEFAULT_FONT_SIZE_NAME_PT
        : variable === "jobTitle"
          ? PRINT_CARD_DEFAULT_FONT_SIZE_TITLE_PT
          : variable === "tagline"
            ? PRINT_CARD_DEFAULT_FONT_SIZE_TAGLINE_PT
            : variable === "companyName"
              ? PRINT_CARD_DEFAULT_FONT_SIZE_COMPANY_PT
              : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT);
    const fontWeight =
      opts?.fontWeight ??
      (variable === "userName"
        ? PRINT_CARD_DEFAULT_FONT_WEIGHT_NAME
        : variable === "jobTitle"
          ? PRINT_CARD_DEFAULT_FONT_WEIGHT_TITLE
          : variable === "tagline"
            ? PRINT_CARD_DEFAULT_FONT_WEIGHT_TAGLINE
            : variable === "companyName"
              ? PRINT_CARD_DEFAULT_FONT_WEIGHT_COMPANY
              : PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY);
    elements.push({
      id: newDesignElementId(),
      type: "text",
      ...box,
      zIndex: z++,
      variable,
      text: opts?.text,
      fontSizePt,
      fontWeight,
    });
  };

  const pushPhoneEmailSquash = (present: boolean, box: LayoutBox): void => {
    if (!present) {
      return;
    }
    const partStyle = {
      fontSizePt: PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT,
      fontWeight: PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY,
    };
    elements.push({
      id: newDesignElementId(),
      type: "text",
      ...box,
      zIndex: z++,
      variable: "squash",
      squashParts: [
        { variable: "phone", ...partStyle },
        { variable: "email", ...partStyle },
      ],
      squashSeparator: PRINT_CARD_DEFAULT_SQUASH_SEPARATOR,
    });
  };

  const hasName = bodyHasClass(body, "pc-name");
  const hasTitle = bodyHasClass(body, "pc-title");
  const hasContact =
    bodyHasClass(body, "pc-email") ||
    bodyHasClass(body, "pc-front__email") ||
    bodyHasClass(body, "pc-contact") ||
    bodyHasClass(body, "pc-front__contact") ||
    /\bdata-pc-var\s*=\s*["']squash["']/i.test(body);
  const hasCompanyName = bodyHasClass(body, "pc-company-name");
  const hasCompanyAddress = bodyHasClass(body, "pc-company-address");
  const hasTagline = bodyHasClass(body, "pc-back__tagline");
  const hasFrontLogo =
    bodyHasClass(body, "pc-logo") || body.includes("${companyImageUrl}");
  const hasBackLogo = bodyHasClass(body, "pc-back__logo");
  const hasQr =
    bodyHasClass(body, "pc-qr") ||
    bodyHasClass(body, "pc-back__qr") ||
    body.includes("${QRdigitalCardUrl}");
  const hasNfc =
    bodyHasClass(body, "pc-nfc") ||
    bodyHasClass(body, "pc-back__nfc") ||
    body.includes("${nfcIcon}");
  const isStack = bodyHasClass(body, "pc-front--stack");
  const isSplit = bodyHasClass(body, "pc-front--split");

  if (side === "front") {
    if (isStack) {
      const brand = layoutAbsBox(
        layoutDecls(cssMap, ".pc-front__brand"),
        page,
        { widthCm: 2.4, heightCm: page.heightCm - 1 },
      );
      const meta = layoutAbsBox(layoutDecls(cssMap, ".pc-front__meta"), page, {
        widthCm: page.widthCm - 5,
        heightCm: page.heightCm - 2,
      });
      if (hasFrontLogo) {
        const logoDecls = layoutDecls(cssMap, ".pc-logo", ".pc-card__logo");
        elements.push({
          id: newDesignElementId(),
          type: "logo",
          ...(logoDecls
            ? layoutAbsBox(logoDecls, page, {
                widthCm: PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
                heightCm: PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
              })
            : {
                xCm: brand.xCm,
                yCm: brand.yCm,
                widthCm: PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
                heightCm: PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
              }),
          zIndex: z++,
          srcVariable: "companyImageUrl",
        });
      }
      if (hasQr) {
        elements.push({
          id: newDesignElementId(),
          type: "qr",
          ...layoutAbsBox(layoutDecls(cssMap, ".pc-qr", ".pc-card__qr"), page, {
            widthCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
            heightCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
          }),
          zIndex: z++,
          srcVariable: "QRdigitalCardUrl",
        });
      }
      const titleGap = layoutMarginTopCm(
        cssMap,
        ".pc-title",
        LAYOUT_STACK_TITLE_GAP_CM,
      );
      const identity = layoutAbsBox(
        layoutDecls(cssMap, ".pc-front__identity"),
        page,
        {
          widthCm: brand.widthCm,
          heightCm:
            LAYOUT_STACK_NAME_LINE_CM + titleGap + LAYOUT_STACK_TITLE_LINE_CM,
        },
      );
      const identityH = roundLayoutCm(
        LAYOUT_STACK_NAME_LINE_CM + titleGap + LAYOUT_STACK_TITLE_LINE_CM,
      );
      const identityY =
        parseLayoutCmValue(
          layoutDecls(cssMap, ".pc-front__identity")?.["top"],
        ) != null ||
        parseLayoutCmValue(
          layoutDecls(cssMap, ".pc-front__identity")?.["bottom"],
        ) != null
          ? identity.yCm
          : roundLayoutCm(brand.yCm + brand.heightCm - identityH);
      const identityW = identity.widthCm || brand.widthCm;
      const identityX = identity.xCm || brand.xCm;
      pushText(
        hasName,
        "userName",
        {
          xCm: identityX,
          yCm: identityY,
          widthCm: identityW,
          heightCm: LAYOUT_STACK_NAME_LINE_CM,
        },
        { fontSizePt: 11 },
      );
      pushText(
        hasTitle,
        "jobTitle",
        {
          xCm: identityX,
          yCm: roundLayoutCm(identityY + LAYOUT_STACK_NAME_LINE_CM + titleGap),
          widthCm: identityW,
          heightCm: LAYOUT_STACK_TITLE_LINE_CM,
        },
        { fontSizePt: 7 },
      );
      const addressGap = layoutMarginTopCm(
        cssMap,
        ".pc-company-address",
        LAYOUT_ADDRESS_STACK_GAP_CM,
      );
      pushText(hasCompanyName, "companyName", {
        xCm: meta.xCm,
        yCm: meta.yCm,
        widthCm: meta.widthCm,
        heightCm: LAYOUT_ADDRESS_ROW_CM,
      });
      pushText(hasCompanyAddress, "companyAddress", {
        xCm: meta.xCm,
        yCm: roundLayoutCm(meta.yCm + LAYOUT_ADDRESS_ROW_CM + addressGap),
        widthCm: meta.widthCm,
        heightCm: LAYOUT_ADDRESS_ROW_CM,
      });
      pushPhoneEmailSquash(hasContact, {
        xCm: meta.xCm,
        yCm: roundLayoutCm(meta.yCm + meta.heightCm - LAYOUT_CONTACT_LINE_CM),
        widthCm: meta.widthCm,
        heightCm: LAYOUT_CONTACT_LINE_CM,
      });
      if (hasNfc) {
        elements.push({
          id: newDesignElementId(),
          type: "nfc",
          ...layoutAbsBox(
            layoutDecls(cssMap, ".pc-nfc", ".pc-card__nfc"),
            page,
            {
              widthCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
              heightCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
            },
          ),
          zIndex: z++,
          srcVariable: "nfcIcon",
        });
      }
    } else if (isSplit) {
      const main = layoutAbsBox(layoutDecls(cssMap, ".pc-front__main"), page, {
        widthCm: page.widthCm - 4,
        heightCm: 1.35,
      });
      const address = layoutAbsBox(
        layoutDecls(cssMap, ".pc-front__address"),
        page,
        { widthCm: page.widthCm - 4, heightCm: 1 },
      );
      const contact = layoutAbsBox(
        layoutDecls(cssMap, ".pc-front__email", ".pc-front__contact"),
        page,
        { widthCm: page.widthCm - 4, heightCm: LAYOUT_CONTACT_LINE_CM },
      );
      if (hasFrontLogo) {
        elements.push({
          id: newDesignElementId(),
          type: "logo",
          ...layoutAbsBox(
            layoutDecls(cssMap, ".pc-logo", ".pc-card__logo") ??
              layoutDecls(cssMap, ".pc-front__brand"),
            page,
            {
              widthCm: PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
              heightCm: PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
            },
          ),
          zIndex: z++,
          srcVariable: "companyImageUrl",
        });
      }
      pushText(hasName, "userName", {
        xCm: main.xCm,
        yCm: main.yCm,
        widthCm: main.widthCm,
        heightCm: LAYOUT_NAME_LINE_CM,
      });
      pushText(
        hasTitle,
        "jobTitle",
        {
          xCm: main.xCm,
          yCm: roundLayoutCm(
            main.yCm +
              LAYOUT_NAME_LINE_CM +
              layoutMarginTopCm(cssMap, ".pc-title", LAYOUT_STACK_TITLE_GAP_CM),
          ),
          widthCm: main.widthCm,
          heightCm: LAYOUT_SPLIT_TITLE_LINE_CM,
        },
        { fontSizePt: 8 },
      );
      pushText(hasCompanyName, "companyName", {
        xCm: address.xCm,
        yCm: address.yCm,
        widthCm: address.widthCm,
        heightCm: LAYOUT_ADDRESS_ROW_CM,
      });
      pushText(hasCompanyAddress, "companyAddress", {
        xCm: address.xCm,
        yCm: roundLayoutCm(
          address.yCm + LAYOUT_ADDRESS_ROW_CM + LAYOUT_ADDRESS_STACK_GAP_CM,
        ),
        widthCm: address.widthCm,
        heightCm: LAYOUT_ADDRESS_ROW_CM,
      });
      pushPhoneEmailSquash(hasContact, contact);
      if (hasQr) {
        elements.push({
          id: newDesignElementId(),
          type: "qr",
          ...layoutAbsBox(layoutDecls(cssMap, ".pc-qr", ".pc-card__qr"), page, {
            widthCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
            heightCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
          }),
          zIndex: z++,
          srcVariable: "QRdigitalCardUrl",
        });
      }
      if (hasNfc) {
        elements.push({
          id: newDesignElementId(),
          type: "nfc",
          ...layoutAbsBox(
            layoutDecls(cssMap, ".pc-nfc", ".pc-card__nfc"),
            page,
            {
              widthCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
              heightCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
            },
          ),
          zIndex: z++,
          srcVariable: "nfcIcon",
        });
      }
    } else {
      const main = layoutAbsBox(layoutDecls(cssMap, ".pc-front__main"), page, {
        widthCm: page.widthCm - 1,
        heightCm: page.heightCm - 3.38,
      });
      const address = layoutAbsBox(
        layoutDecls(cssMap, ".pc-front__address"),
        page,
        {
          widthCm: page.widthCm - 1,
          heightCm: LAYOUT_ADDRESS_ROW_CM,
        },
      );
      if (hasFrontLogo) {
        elements.push({
          id: newDesignElementId(),
          type: "logo",
          ...layoutAbsBox(
            layoutDecls(cssMap, ".pc-logo", ".pc-card__logo"),
            page,
            {
              widthCm: PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM,
              heightCm: PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM,
            },
          ),
          zIndex: z++,
          srcVariable: "companyImageUrl",
        });
      }
      const titleGap = layoutMarginTopCm(
        cssMap,
        ".pc-title",
        LAYOUT_STANDARD_TITLE_GAP_CM,
      );
      const contactGap = layoutMarginTopCm(
        cssMap,
        ".pc-email",
        layoutMarginTopCm(
          cssMap,
          ".pc-contact",
          LAYOUT_STANDARD_CONTACT_GAP_CM,
        ),
      );
      pushText(hasName, "userName", {
        xCm: main.xCm,
        yCm: main.yCm,
        widthCm: main.widthCm,
        heightCm: LAYOUT_NAME_LINE_CM,
      });
      const titleY = roundLayoutCm(main.yCm + LAYOUT_NAME_LINE_CM + titleGap);
      pushText(hasTitle, "jobTitle", {
        xCm: main.xCm,
        yCm: titleY,
        widthCm: main.widthCm,
        heightCm: LAYOUT_TITLE_LINE_CM,
      });
      pushPhoneEmailSquash(hasContact, {
        xCm: main.xCm,
        yCm: roundLayoutCm(titleY + LAYOUT_TITLE_LINE_CM + contactGap),
        widthCm: main.widthCm,
        heightCm: LAYOUT_CONTACT_LINE_CM,
      });
      const companyNameW = roundLayoutCm(address.widthCm * 0.38);
      const companyGap = 0.15;
      pushText(hasCompanyName, "companyName", {
        xCm: address.xCm,
        yCm: address.yCm,
        widthCm: companyNameW,
        heightCm: address.heightCm,
      });
      pushText(hasCompanyAddress, "companyAddress", {
        xCm: roundLayoutCm(address.xCm + companyNameW + companyGap),
        yCm: address.yCm,
        widthCm: roundLayoutCm(
          Math.max(0.5, address.widthCm - companyNameW - companyGap),
        ),
        heightCm: address.heightCm,
      });
      if (hasQr) {
        elements.push({
          id: newDesignElementId(),
          type: "qr",
          ...layoutAbsBox(layoutDecls(cssMap, ".pc-qr", ".pc-card__qr"), page, {
            widthCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
            heightCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
          }),
          zIndex: z++,
          srcVariable: "QRdigitalCardUrl",
        });
      }
      if (hasNfc) {
        elements.push({
          id: newDesignElementId(),
          type: "nfc",
          ...layoutAbsBox(
            layoutDecls(cssMap, ".pc-nfc", ".pc-card__nfc"),
            page,
            {
              widthCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
              heightCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
            },
          ),
          zIndex: z++,
          srcVariable: "nfcIcon",
        });
      }
    }
  } else {
    if (hasTagline) {
      pushText(
        true,
        "tagline",
        layoutAbsBox(layoutDecls(cssMap, ".pc-back__tagline"), page, {
          widthCm: page.widthCm - 1,
          heightCm: LAYOUT_TAGLINE_MAX_H_CM,
        }),
        { text: PRINT_CARD_DEFAULT_TAGLINE_TEXT },
      );
    }
    if (
      hasBackLogo ||
      (hasFrontLogo && !hasTagline && body.includes("${companyImageUrl}"))
    ) {
      const logoDecls = layoutDecls(
        cssMap,
        ".pc-back__logo",
        ".pc-logo",
        ".pc-card__logo",
      );
      const explicitW = parseLayoutCmValue(logoDecls?.["width"]);
      const explicitH = parseLayoutCmValue(logoDecls?.["height"]);
      const maxW = parseLayoutMaxWidthCm(
        logoDecls?.["max-width"],
        page.widthCm,
      );
      const maxH = parseLayoutCmValue(logoDecls?.["max-height"]);
      const widthCm = roundLayoutCm(
        Math.max(
          PRINT_CARD_MIN_SIZE_CM,
          explicitW ??
            Math.min(
              maxW ?? PRINT_CARD_BACK_LOGO_MAX_WIDTH_CM,
              page.widthCm - 1,
            ),
        ),
      );
      const heightCm = roundLayoutCm(
        Math.max(
          PRINT_CARD_MIN_SIZE_CM,
          explicitH ?? maxH ?? PRINT_CARD_BACK_LOGO_MAX_HEIGHT_CM,
        ),
      );
      const positionDecls: Record<string, string> = {
        ...(logoDecls ?? {}),
        width: `${widthCm}cm`,
        height: `${heightCm}cm`,
      };
      delete positionDecls["max-width"];
      delete positionDecls["max-height"];
      const objectPos = (
        positionDecls["object-position"] ??
        logoDecls?.["object-position"] ??
        ""
      )
        .trim()
        .toLowerCase();
      elements.push({
        id: newDesignElementId(),
        type: "logo",
        ...layoutAbsBox(positionDecls, page, { widthCm, heightCm }),
        zIndex: z++,
        srcVariable: "companyImageUrl",
        align: /\bright\b/.test(objectPos)
          ? "right"
          : /\bleft\b/.test(objectPos)
            ? "left"
            : "center",
      });
    }
    if (hasQr) {
      elements.push({
        id: newDesignElementId(),
        type: "qr",
        ...layoutAbsBox(
          layoutDecls(cssMap, ".pc-back__qr", ".pc-qr", ".pc-card__qr"),
          page,
          {
            widthCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
            heightCm: PRINT_CARD_DEFAULT_QR_SIZE_CM,
          },
        ),
        zIndex: z++,
        srcVariable: "QRdigitalCardUrl",
      });
    }
    if (hasNfc) {
      elements.push({
        id: newDesignElementId(),
        type: "nfc",
        ...layoutAbsBox(
          layoutDecls(cssMap, ".pc-back__nfc", ".pc-nfc", ".pc-card__nfc"),
          page,
          {
            widthCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
            heightCm: PRINT_CARD_DEFAULT_NFC_SIZE_CM,
          },
        ),
        zIndex: z++,
        srcVariable: "nfcIcon",
      });
    }
  }

  return {
    backgroundColor: "#FFFFFF",
    backgroundImageUrl: null,
    elements: elements.map((el) => {
      const name = el.name?.trim() || printCardElementDefaultName(el);
      return name === el.name ? el : { ...el, name };
    }),
  };
}

function rotationDeg(deg: number): number {
  if (!Number.isFinite(deg)) {
    return 0;
  }
  let next = deg % 360;
  if (next > 180) {
    next -= 360;
  } else if (next <= -180) {
    next += 360;
  }
  return Math.round(next * 10) / 10;
}

function roundCm(value: number): number {
  return Math.round(value * 100) / 100;
}

export function printCardElementAabb(box: PrintCardGeometryBox): PrintCardAabb {
  const width = Math.max(0, box.widthCm);
  const height = Math.max(0, box.heightCm);
  const rad = (rotationDeg(box.rotationDeg ?? 0) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const aabbW = width * cos + height * sin;
  const aabbH = width * sin + height * cos;
  const centerX = box.xCm + width / 2;
  const centerY = box.yCm + height / 2;
  return {
    left: centerX - aabbW / 2,
    top: centerY - aabbH / 2,
    right: centerX + aabbW / 2,
    bottom: centerY + aabbH / 2,
    width: aabbW,
    height: aabbH,
    centerX,
    centerY,
  };
}

export function printCardVisualPosition(box: PrintCardGeometryBox): {
  xCm: number;
  yCm: number;
} {
  const aabb = printCardElementAabb(box);
  return {
    xCm: roundCm(aabb.left),
    yCm: roundCm(aabb.top),
  };
}

export function printCardCssPositionFromVisual(params: {
  visualXCm: number;
  visualYCm: number;
  widthCm: number;
  heightCm: number;
  rotationDeg?: number;
}): { xCm: number; yCm: number } {
  const aabb = printCardElementAabb({
    xCm: 0,
    yCm: 0,
    widthCm: params.widthCm,
    heightCm: params.heightCm,
    rotationDeg: params.rotationDeg,
  });
  return {
    xCm: roundCm(params.visualXCm + aabb.width / 2 - params.widthCm / 2),
    yCm: roundCm(params.visualYCm + aabb.height / 2 - params.heightCm / 2),
  };
}

export function clampPrintCardElementBox(
  box: PrintCardGeometryBox,
  page: {
    widthCm: number;
    heightCm: number;
    originXCm?: number;
    originYCm?: number;
    safeZoneInsetMm?: number;
  },
  square = false,
): { xCm: number; yCm: number; widthCm: number; heightCm: number } {
  const hasOrigin =
    typeof page.originXCm === "number" && typeof page.originYCm === "number";
  const insetCm = hasOrigin
    ? 0
    : Math.round(Math.max(0, page.safeZoneInsetMm ?? 0) * 10) / 100;
  const originX = hasOrigin ? page.originXCm : insetCm;
  const originY = hasOrigin ? page.originYCm : insetCm;
  const innerW = hasOrigin
    ? page.widthCm
    : Math.max(0, Math.round((page.widthCm - insetCm * 2) * 100) / 100);
  const innerH = hasOrigin
    ? page.heightCm
    : Math.max(0, Math.round((page.heightCm - insetCm * 2) * 100) / 100);
  const pageW = innerW > 0 ? innerW : Number.POSITIVE_INFINITY;
  const pageH = innerH > 0 ? innerH : Number.POSITIVE_INFINITY;
  let w = roundCm(
    Math.min(Math.max(PRINT_CARD_MIN_SIZE_CM, box.widthCm), pageW),
  );
  let h = roundCm(
    Math.min(Math.max(PRINT_CARD_MIN_SIZE_CM, box.heightCm), pageH),
  );
  if (square) {
    const size = Math.min(w, h);
    w = size;
    h = size;
  }
  const rad = (rotationDeg(box.rotationDeg ?? 0) * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const aabbW = w * cos + h * sin;
  const aabbH = w * sin + h * cos;
  if (aabbW > pageW || aabbH > pageH) {
    const scale = Math.min(
      pageW / Math.max(aabbW, 1e-6),
      pageH / Math.max(aabbH, 1e-6),
      1,
    );
    w = roundCm(Math.max(PRINT_CARD_MIN_SIZE_CM, w * scale));
    h = roundCm(Math.max(PRINT_CARD_MIN_SIZE_CM, h * scale));
    if (square) {
      const size = Math.min(w, h);
      w = size;
      h = size;
    }
  }
  const aabbW2 = w * cos + h * sin;
  const aabbH2 = w * sin + h * cos;
  const cx = box.xCm + w / 2;
  const cy = box.yCm + h / 2;
  const minCx = originX + aabbW2 / 2;
  const maxCx = Math.max(minCx, originX + pageW - aabbW2 / 2);
  const minCy = originY + aabbH2 / 2;
  const maxCy = Math.max(minCy, originY + pageH - aabbH2 / 2);
  return {
    xCm: roundCm(Math.min(Math.max(cx, minCx), maxCx) - w / 2),
    yCm: roundCm(Math.min(Math.max(cy, minCy), maxCy) - h / 2),
    widthCm: Math.min(w, pageW),
    heightCm: Math.min(h, pageH),
  };
}

export const PRINT_CARD_CM_TO_PX = 37.795275591;
export const PRINT_CARD_SNAP_PX = 6;
const ALIGN_MATCH_CM = 0.012;
const MIN_BAND_CM = 1 / PRINT_CARD_CM_TO_PX;

interface GuideBox {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

interface AxisSnap {
  delta: number;
  lines: SmartGuideLine[];
  spacings: SmartGuideSpacing[];
}

export function printCardSnapThresholdCm(scale: number): number {
  const safeScale = Math.max(scale, 0.15);
  return PRINT_CARD_SNAP_PX / (PRINT_CARD_CM_TO_PX * safeScale);
}

export function snapPrintCardMove(input: SnapMoveInput): SnapMoveResult {
  const movingBox = boxFromGeometry(input.moving);
  const siblingBoxes = input.siblings
    .filter((el) => el.id !== input.moving.id)
    .map(boxFromGeometry);
  const pageBox = pageToBox(input.page);
  if (!input.enabled) {
    const clamped = clampBoxToPage(movingBox, pageBox);
    const view = input.measure
      ? measureView(clamped, siblingBoxes, pageBox)
      : emptyView();
    return {
      xCm: roundCm(input.moving.xCm + (clamped.left - movingBox.left)),
      yCm: roundCm(input.moving.yCm + (clamped.top - movingBox.top)),
      view,
    };
  }
  const xSnap = snapAxis({
    moving: movingBox,
    siblings: siblingBoxes,
    page: pageBox,
    thresholdCm: input.thresholdCm,
    axis: "x",
    allowSpacing: true,
  });
  const ySnap = snapAxis({
    moving: movingBox,
    siblings: siblingBoxes,
    page: pageBox,
    thresholdCm: input.thresholdCm,
    axis: "y",
    allowSpacing: true,
  });
  const next = translateBox(movingBox, xSnap.delta, ySnap.delta);
  const clamped = clampBoxToPage(next, pageBox);
  const dx = clamped.left - movingBox.left;
  const dy = clamped.top - movingBox.top;
  const aligned = collectAlignment(clamped, siblingBoxes, pageBox);
  const spacings = input.measure
    ? collectNearestSpacings(clamped, siblingBoxes, pageBox)
    : [...xSnap.spacings, ...ySnap.spacings];
  return {
    xCm: roundCm(input.moving.xCm + dx),
    yCm: roundCm(input.moving.yCm + dy),
    view: {
      lines: aligned.lines,
      spacings: mergeSpacings(spacings),
    },
  };
}

export function snapPrintCardResize(input: SnapResizeInput): SnapResizeResult {
  const start: SnapResizeResult = {
    xCm: roundCm(input.moving.xCm),
    yCm: roundCm(input.moving.yCm),
    widthCm: roundCm(input.moving.widthCm),
    heightCm: roundCm(input.moving.heightCm),
    view: emptyView(),
  };
  const siblingBoxes = input.siblings
    .filter((el) => el.id !== input.moving.id)
    .map(boxFromGeometry);
  const pageBox = pageToBox(input.page);
  const movingBox = unrotatedBox(input.moving);
  if (!input.enabled) {
    const clamped = clampBoxToPage(movingBox, pageBox);
    start.xCm = roundCm(clamped.left);
    start.yCm = roundCm(clamped.top);
    start.widthCm = roundCm(clamped.width);
    start.heightCm = roundCm(clamped.height);
    start.view = input.measure
      ? measureView(clamped, siblingBoxes, pageBox)
      : emptyView();
    return start;
  }
  const handle = input.handle.toLowerCase();
  let left = movingBox.left;
  let top = movingBox.top;
  let right = movingBox.right;
  let bottom = movingBox.bottom;
  if (handle.includes("e")) {
    right += snapEdge(
      movingBox.right,
      xTargets(siblingBoxes, pageBox),
      input.thresholdCm,
    );
  }
  if (handle.includes("w")) {
    left += snapEdge(
      movingBox.left,
      xTargets(siblingBoxes, pageBox),
      input.thresholdCm,
    );
  }
  if (handle.includes("s")) {
    bottom += snapEdge(
      movingBox.bottom,
      yTargets(siblingBoxes, pageBox),
      input.thresholdCm,
    );
  }
  if (handle.includes("n")) {
    top += snapEdge(
      movingBox.top,
      yTargets(siblingBoxes, pageBox),
      input.thresholdCm,
    );
  }
  if (input.keepSquare) {
    const sized = applySquareHandle(
      handle,
      movingBox,
      left,
      top,
      right,
      bottom,
    );
    left = sized.left;
    top = sized.top;
    right = sized.right;
    bottom = sized.bottom;
  }
  const width = Math.max(PRINT_CARD_MIN_SIZE_CM, right - left);
  const height = Math.max(PRINT_CARD_MIN_SIZE_CM, bottom - top);
  right = left + width;
  bottom = top + height;
  const next = boxFromRect(input.moving.id, left, top, right, bottom);
  const clamped = clampBoxToPage(next, pageBox);
  const aligned = collectAlignment(clamped, siblingBoxes, pageBox);
  return {
    xCm: roundCm(clamped.left),
    yCm: roundCm(clamped.top),
    widthCm: roundCm(clamped.width),
    heightCm: roundCm(clamped.height),
    view: {
      lines: aligned.lines,
      spacings: input.measure
        ? mergeSpacings(collectNearestSpacings(clamped, siblingBoxes, pageBox))
        : [],
    },
  };
}

function snapAxis(params: {
  moving: GuideBox;
  siblings: GuideBox[];
  page: GuideBox;
  thresholdCm: number;
  axis: SmartGuideAxis;
  allowSpacing: boolean;
}): AxisSnap {
  const align = bestAlignmentDelta(
    params.moving,
    params.siblings,
    params.page,
    params.axis,
    params.thresholdCm,
  );
  const spacing = params.allowSpacing
    ? bestSpacingDelta(
        params.moving,
        params.siblings,
        params.axis,
        params.thresholdCm,
      )
    : { delta: Number.POSITIVE_INFINITY, spacings: [] as SmartGuideSpacing[] };
  const spacingAbs = Math.abs(spacing.delta);
  const alignAbs = Math.abs(align.delta);
  const spacingReady =
    Number.isFinite(spacing.delta) && spacingAbs <= params.thresholdCm;
  const alignReady =
    Number.isFinite(align.delta) && alignAbs <= params.thresholdCm;
  if (
    spacingReady &&
    (!alignReady ||
      spacingAbs + 1e-9 < alignAbs ||
      (spacing.spacings.length >= 2 && spacingAbs <= alignAbs + 1e-9))
  ) {
    return { delta: spacing.delta, lines: [], spacings: spacing.spacings };
  }
  if (alignReady) {
    return { delta: align.delta, lines: align.lines, spacings: [] };
  }
  return { delta: 0, lines: [], spacings: [] };
}

function bestAlignmentDelta(
  moving: GuideBox,
  siblings: GuideBox[],
  page: GuideBox,
  axis: SmartGuideAxis,
  thresholdCm: number,
): { delta: number; lines: SmartGuideLine[] } {
  const movingAnchors = axisAnchors(moving, axis);
  const targets =
    axis === "x" ? xTargets(siblings, page) : yTargets(siblings, page);
  let bestDelta = Number.POSITIVE_INFINITY;
  let bestAbs = thresholdCm + 1;
  for (const anchor of movingAnchors) {
    for (const target of targets) {
      const delta = target.value - anchor.value;
      const abs = Math.abs(delta);
      if (abs > thresholdCm || abs > bestAbs + 1e-9) {
        continue;
      }
      const preferCenter =
        abs === bestAbs &&
        (target.kind === "center" || anchor.kind === "center") &&
        bestDelta !== 0;
      if (abs < bestAbs - 1e-9 || preferCenter) {
        bestAbs = abs;
        bestDelta = delta;
      }
    }
  }
  if (!Number.isFinite(bestDelta) || bestAbs > thresholdCm) {
    return { delta: Number.POSITIVE_INFINITY, lines: [] };
  }
  const snapped = translateBox(
    moving,
    axis === "x" ? bestDelta : 0,
    axis === "y" ? bestDelta : 0,
  );
  return {
    delta: bestDelta,
    lines: collectAlignment(snapped, siblings, page).lines.filter(
      (line) => line.axis === axis,
    ),
  };
}

function bestSpacingDelta(
  moving: GuideBox,
  siblings: GuideBox[],
  axis: SmartGuideAxis,
  thresholdCm: number,
): { delta: number; spacings: SmartGuideSpacing[] } {
  const before = nearestBandNeighbor(moving, siblings, axis, "before");
  const after = nearestBandNeighbor(moving, siblings, axis, "after");
  if (!before || !after) {
    return { delta: Number.POSITIVE_INFINITY, spacings: [] };
  }
  const beforeEnd = axis === "x" ? before.right : before.bottom;
  const afterStart = axis === "x" ? after.left : after.top;
  const size = axis === "x" ? moving.width : moving.height;
  const room = afterStart - beforeEnd;
  if (room < size + 1e-6) {
    return { delta: Number.POSITIVE_INFINITY, spacings: [] };
  }
  const delta =
    beforeEnd + (room - size) / 2 - (axis === "x" ? moving.left : moving.top);
  if (Math.abs(delta) > thresholdCm) {
    return { delta: Number.POSITIVE_INFINITY, spacings: [] };
  }
  const snapped = translateBox(
    moving,
    axis === "x" ? delta : 0,
    axis === "y" ? delta : 0,
  );
  return {
    delta,
    spacings: [
      spacingBetween(before, snapped, axis, true),
      spacingBetween(snapped, after, axis, true),
    ].filter((item): item is SmartGuideSpacing => item !== null),
  };
}

function collectAlignment(
  moving: GuideBox,
  siblings: GuideBox[],
  page: GuideBox,
): { lines: SmartGuideLine[] } {
  const lines: SmartGuideLine[] = [];
  const targets = [...siblings, page];
  for (const target of targets) {
    const xHits = matchedAnchors(moving, target, "x");
    for (const hit of xHits) {
      lines.push({
        axis: "x",
        positionCm: hit.value,
        startCm: Math.min(moving.top, target.top),
        endCm: Math.max(moving.bottom, target.bottom),
        kind: hit.kind,
      });
    }
    const yHits = matchedAnchors(moving, target, "y");
    for (const hit of yHits) {
      lines.push({
        axis: "y",
        positionCm: hit.value,
        startCm: Math.min(moving.left, target.left),
        endCm: Math.max(moving.right, target.right),
        kind: hit.kind,
      });
    }
  }
  return { lines: mergeLines(lines) };
}

function collectNearestSpacings(
  moving: GuideBox,
  siblings: GuideBox[],
  page: GuideBox,
): SmartGuideSpacing[] {
  const items: SmartGuideSpacing[] = [];
  const left = nearestInDirection(moving, siblings, "left");
  const right = nearestInDirection(moving, siblings, "right");
  const top = nearestInDirection(moving, siblings, "up");
  const bottom = nearestInDirection(moving, siblings, "down");
  if (left) {
    items.push(left);
  } else {
    const toPage = spacingBetween(
      boxFromRect(
        "page-left",
        page.left,
        moving.top,
        moving.left,
        moving.bottom,
      ),
      moving,
      "x",
      false,
    );
    if (toPage && moving.left > page.left + ALIGN_MATCH_CM) {
      items.push(toPage);
    }
  }
  if (right) {
    items.push(right);
  } else if (page.right - moving.right > ALIGN_MATCH_CM) {
    const toPage = spacingBetween(
      moving,
      boxFromRect(
        "page-right",
        moving.right,
        moving.top,
        page.right,
        moving.bottom,
      ),
      "x",
      false,
    );
    if (toPage) {
      items.push(toPage);
    }
  }
  if (top) {
    items.push(top);
  } else if (moving.top > page.top + ALIGN_MATCH_CM) {
    const toPage = spacingBetween(
      boxFromRect("page-top", moving.left, page.top, moving.right, moving.top),
      moving,
      "y",
      false,
    );
    if (toPage) {
      items.push(toPage);
    }
  }
  if (bottom) {
    items.push(bottom);
  } else if (page.bottom - moving.bottom > ALIGN_MATCH_CM) {
    const toPage = spacingBetween(
      moving,
      boxFromRect(
        "page-bottom",
        moving.left,
        moving.bottom,
        moving.right,
        page.bottom,
      ),
      "y",
      false,
    );
    if (toPage) {
      items.push(toPage);
    }
  }
  return items;
}

function measureView(
  moving: GuideBox,
  siblings: GuideBox[],
  page: GuideBox,
): SmartGuidesView {
  return {
    lines: collectAlignment(moving, siblings, page).lines,
    spacings: collectNearestSpacings(moving, siblings, page),
  };
}

function nearestInDirection(
  moving: GuideBox,
  siblings: GuideBox[],
  direction: "left" | "right" | "up" | "down",
): SmartGuideSpacing | null {
  const axis: SmartGuideAxis =
    direction === "left" || direction === "right" ? "x" : "y";
  let best: GuideBox | null = null;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const sibling of siblings) {
    if (!bandOverlap(moving, sibling, axis)) {
      continue;
    }
    let gap = Number.POSITIVE_INFINITY;
    if (direction === "left" && sibling.right <= moving.left) {
      gap = moving.left - sibling.right;
    } else if (direction === "right" && sibling.left >= moving.right) {
      gap = sibling.left - moving.right;
    } else if (direction === "up" && sibling.bottom <= moving.top) {
      gap = moving.top - sibling.bottom;
    } else if (direction === "down" && sibling.top >= moving.bottom) {
      gap = sibling.top - moving.bottom;
    }
    if (gap < bestGap) {
      bestGap = gap;
      best = sibling;
    }
  }
  if (!best || !Number.isFinite(bestGap)) {
    return null;
  }
  if (direction === "left" || direction === "up") {
    return spacingBetween(best, moving, axis, false);
  }
  return spacingBetween(moving, best, axis, false);
}

function spacingBetween(
  a: GuideBox,
  b: GuideBox,
  axis: SmartGuideAxis,
  equal: boolean,
): SmartGuideSpacing | null {
  if (axis === "x") {
    const size = b.left - a.right;
    if (size <= ALIGN_MATCH_CM) {
      return null;
    }
    const overlapTop = Math.max(a.top, b.top);
    const overlapBottom = Math.min(a.bottom, b.bottom);
    const mid =
      overlapTop < overlapBottom
        ? (overlapTop + overlapBottom) / 2
        : (a.centerY + b.centerY) / 2;
    return {
      axis: "x",
      xCm: a.right,
      yCm: mid,
      widthCm: size,
      heightCm: 0,
      sizeCm: roundCm(size),
      equal,
    };
  }
  const size = b.top - a.bottom;
  if (size <= ALIGN_MATCH_CM) {
    return null;
  }
  const overlapLeft = Math.max(a.left, b.left);
  const overlapRight = Math.min(a.right, b.right);
  const mid =
    overlapLeft < overlapRight
      ? (overlapLeft + overlapRight) / 2
      : (a.centerX + b.centerX) / 2;
  return {
    axis: "y",
    xCm: mid,
    yCm: a.bottom,
    widthCm: 0,
    heightCm: size,
    sizeCm: roundCm(size),
    equal,
  };
}

function nearestBandNeighbor(
  box: GuideBox,
  others: GuideBox[],
  axis: SmartGuideAxis,
  side: "before" | "after",
): GuideBox | null {
  let best: GuideBox | null = null;
  let bestEdge =
    side === "before" ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  for (const other of others) {
    if (other.id === box.id || !bandOverlap(box, other, axis)) {
      continue;
    }
    if (side === "before") {
      const otherEnd = axis === "x" ? other.right : other.bottom;
      const boxStart = axis === "x" ? box.left : box.top;
      if (otherEnd > boxStart + 1e-9 || otherEnd <= bestEdge) {
        continue;
      }
      bestEdge = otherEnd;
      best = other;
      continue;
    }
    const otherStart = axis === "x" ? other.left : other.top;
    const boxEnd = axis === "x" ? box.right : box.bottom;
    if (otherStart < boxEnd - 1e-9 || otherStart >= bestEdge) {
      continue;
    }
    bestEdge = otherStart;
    best = other;
  }
  return best;
}

function matchedAnchors(
  moving: GuideBox,
  target: GuideBox,
  axis: SmartGuideAxis,
): { value: number; kind: "edge" | "center" }[] {
  const hits: { value: number; kind: "edge" | "center" }[] = [];
  const movingAnchors = axisAnchors(moving, axis);
  const targetAnchors = axisAnchors(target, axis);
  for (const from of movingAnchors) {
    for (const to of targetAnchors) {
      if (Math.abs(from.value - to.value) <= ALIGN_MATCH_CM) {
        hits.push({
          value: to.value,
          kind:
            from.kind === "center" || to.kind === "center" ? "center" : "edge",
        });
      }
    }
  }
  return hits;
}

function axisAnchors(
  box: GuideBox,
  axis: SmartGuideAxis,
): { kind: "edge" | "center"; value: number }[] {
  if (axis === "x") {
    return [
      { kind: "edge", value: box.left },
      { kind: "center", value: box.centerX },
      { kind: "edge", value: box.right },
    ];
  }
  return [
    { kind: "edge", value: box.top },
    { kind: "center", value: box.centerY },
    { kind: "edge", value: box.bottom },
  ];
}

function xTargets(
  siblings: GuideBox[],
  page: GuideBox,
): { value: number; kind: "edge" | "center" }[] {
  return [
    { value: page.left, kind: "edge" },
    { value: page.centerX, kind: "center" },
    { value: page.right, kind: "edge" },
    ...siblings.flatMap((box) => axisAnchors(box, "x")),
  ];
}

function yTargets(
  siblings: GuideBox[],
  page: GuideBox,
): { value: number; kind: "edge" | "center" }[] {
  return [
    { value: page.top, kind: "edge" },
    { value: page.centerY, kind: "center" },
    { value: page.bottom, kind: "edge" },
    ...siblings.flatMap((box) => axisAnchors(box, "y")),
  ];
}

function snapEdge(
  current: number,
  targets: { value: number }[],
  thresholdCm: number,
): number {
  let best = 0;
  let bestAbs = thresholdCm + 1;
  for (const target of targets) {
    const delta = target.value - current;
    const abs = Math.abs(delta);
    if (abs <= thresholdCm && abs < bestAbs) {
      bestAbs = abs;
      best = delta;
    }
  }
  return bestAbs <= thresholdCm ? best : 0;
}

function applySquareHandle(
  handle: string,
  start: GuideBox,
  left: number,
  top: number,
  right: number,
  bottom: number,
): { left: number; top: number; right: number; bottom: number } {
  const fromW = handle.includes("e")
    ? right - start.left
    : handle.includes("w")
      ? start.right - left
      : null;
  const fromH = handle.includes("s")
    ? bottom - start.top
    : handle.includes("n")
      ? start.bottom - top
      : null;
  let size = start.width;
  if (fromW !== null && fromH !== null) {
    size =
      Math.abs(fromW - start.width) >= Math.abs(fromH - start.height)
        ? fromW
        : fromH;
  } else if (fromW !== null) {
    size = fromW;
  } else if (fromH !== null) {
    size = fromH;
  }
  size = Math.max(PRINT_CARD_MIN_SIZE_CM, size);
  return {
    left: handle.includes("w") ? start.right - size : start.left,
    top: handle.includes("n") ? start.bottom - size : start.top,
    right: handle.includes("w") ? start.right : start.left + size,
    bottom: handle.includes("n") ? start.bottom : start.top + size,
  };
}

function boxFromGeometry(el: PrintCardGuideGeometry): GuideBox {
  const aabb = printCardElementAabb(el);
  return {
    id: el.id,
    left: aabb.left,
    top: aabb.top,
    right: aabb.right,
    bottom: aabb.bottom,
    width: aabb.width,
    height: aabb.height,
    centerX: aabb.centerX,
    centerY: aabb.centerY,
  };
}

function unrotatedBox(el: PrintCardGuideGeometry): GuideBox {
  return boxFromRect(
    el.id,
    el.xCm,
    el.yCm,
    el.xCm + el.widthCm,
    el.yCm + el.heightCm,
  );
}

function pageToBox(page: PrintCardGuidePage): GuideBox {
  const originX = page.originXCm ?? 0;
  const originY = page.originYCm ?? 0;
  return boxFromRect(
    "__page__",
    originX,
    originY,
    originX + page.widthCm,
    originY + page.heightCm,
  );
}

function boxFromRect(
  id: string,
  left: number,
  top: number,
  right: number,
  bottom: number,
): GuideBox {
  const width = right - left;
  const height = bottom - top;
  return {
    id,
    left,
    top,
    right,
    bottom,
    width,
    height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

function translateBox(box: GuideBox, dx: number, dy: number): GuideBox {
  return boxFromRect(
    box.id,
    box.left + dx,
    box.top + dy,
    box.right + dx,
    box.bottom + dy,
  );
}

function clampBoxToPage(box: GuideBox, page: GuideBox): GuideBox {
  const width = Math.min(
    Math.max(PRINT_CARD_MIN_SIZE_CM, box.width),
    page.width,
  );
  const height = Math.min(
    Math.max(PRINT_CARD_MIN_SIZE_CM, box.height),
    page.height,
  );
  const minCx = page.left + width / 2;
  const maxCx = Math.max(minCx, page.right - width / 2);
  const minCy = page.top + height / 2;
  const maxCy = Math.max(minCy, page.bottom - height / 2);
  const cx = Math.min(Math.max(box.centerX, minCx), maxCx);
  const cy = Math.min(Math.max(box.centerY, minCy), maxCy);
  return boxFromRect(
    box.id,
    cx - width / 2,
    cy - height / 2,
    cx + width / 2,
    cy + height / 2,
  );
}

function bandOverlap(a: GuideBox, b: GuideBox, axis: SmartGuideAxis): boolean {
  const overlap =
    axis === "x"
      ? Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top)
      : Math.min(a.right, b.right) - Math.max(a.left, b.left);
  return overlap >= MIN_BAND_CM;
}

function mergeLines(lines: SmartGuideLine[]): SmartGuideLine[] {
  const merged = new Map<string, SmartGuideLine>();
  for (const line of lines) {
    if (line.endCm - line.startCm < 1e-6) {
      continue;
    }
    const key = `${line.axis}:${roundCm(line.positionCm)}:${line.kind}`;
    const prev = merged.get(key);
    if (!prev) {
      merged.set(key, { ...line, positionCm: roundCm(line.positionCm) });
      continue;
    }
    prev.startCm = Math.min(prev.startCm, line.startCm);
    prev.endCm = Math.max(prev.endCm, line.endCm);
  }
  return [...merged.values()];
}

function mergeSpacings(spacings: SmartGuideSpacing[]): SmartGuideSpacing[] {
  const merged = new Map<string, SmartGuideSpacing>();
  for (const spacing of spacings) {
    const key = `${spacing.axis}:${roundCm(spacing.xCm)}:${roundCm(spacing.yCm)}:${roundCm(spacing.sizeCm)}`;
    const prev = merged.get(key);
    if (!prev) {
      merged.set(key, { ...spacing, sizeCm: roundCm(spacing.sizeCm) });
      continue;
    }
    prev.equal = prev.equal || spacing.equal;
  }
  return [...merged.values()];
}

function emptyView(): SmartGuidesView {
  return { lines: [], spacings: [] };
}
